import {
  CoreLanguageType,
  LanguageType,
  LocaleDetails,
  proxyLanguageRecord,
} from 'shared/src/types/languages.type';

interface Option {
  label: string;
  value: string | number;
}

type ApplicationFields = {
  id: string;
  label: string;
  type?: string;
  required: boolean;
  prefix?: string;
};

type RadioGroups = {
  question: string;
  name: string;
  options: Option[];
}[];

type CultureValues = {
  component: string;
  title: string;
  details: {
    title: string;
    description: string;
  }[];
};

type CareerMeta = {
  title: string;
  description: string;
};

type CareersPageContent = {
  // Page header
  header: {
    badge: string;
    title: {
      part1: string;
      highlighted: string;
    };
    description: string;
  };

  // Positions section
  positions: {
    sectionBadge: string;
    sectionTitle: {
      part1: string;
      highlighted: string;
    };
    sectionDescription: string;
  };

  // No positions available
  noPositions: {
    title: {
      part1: string;
      highlighted: string;
    };
    description: string;
    contactButton: string;
  };

  // Career-related content from ui-content
  careers: string;
  carrerFounderMessage: string;
  careerIntroduction: string;
  careerMeta: CareerMeta;
  cultureValues: CultureValues;
  fields: ApplicationFields[];
  radioGroups: RadioGroups;
  submit: string;
  requirements: string;
  responsibilities: string;
  uploadCV: string;
  viewMore: string;
  linkInput: {
    smartExtract: string;
    apply: string;
    pasteHelper: string;
    placeholderDefault: string;
  };
};

const careersPageContentCore: Record<CoreLanguageType, CareersPageContent> = {
  // English
  en: {
    header: {
      badge: 'CAREERS',
      title: {
        part1: 'Join Us',
        highlighted: 'Create, Connect, Innovate, and Go Beyond.',
      },
      description:
        'Explore exciting opportunities to join our innovative team and help shape the future of AI-powered product discovery.',
    },
    positions: {
      sectionBadge: 'Open Positions',
      sectionTitle: {
        part1: 'Find Your',
        highlighted: 'Perfect Role',
      },
      sectionDescription:
        'Explore exciting opportunities to join our innovative team and help shape the future of AI-powered product discovery.',
    },
    noPositions: {
      title: {
        part1: 'No Open Positions',
        highlighted: 'Right Now',
      },
      description:
        "We're always looking for talented individuals. Check back soon or reach out to us directly to discuss future opportunities.",
      contactButton: 'Get in Touch',
    },
    careers: 'Careers',
    carrerFounderMessage: `I recognize that it's sometimes hard to judge a book by its cover, and we are committed to reviewing each application thoroughly and fairly. If there's a potential fit, we will contact you promptly. If you feel strongly about your alignment with our team, please reach out to us directly on LinkedIn. We appreciate the time you've taken to apply.
    
    -Oli, Inception Founder`,
    careerIntroduction:
      "Our mission at Inception is to help consumers worldwide find the best products in their region with artificial intelligence. With that goal in mind, we're also doing our best to help local small businesses owners enhance their online presence.\n\nJoin us in solving real-world problems with AI by helping local businesses and working on outstanding technologies. Together, we can make a meaningful impact on both communities and consumers.",
    careerMeta: {
      title: 'Career - Join Us at inceptionai.ca',
      description:
        'Join us in solving real-world problems with AI by transforming local economies and delivering outstanding products. Together, we can make a meaningful impact on both communities and consumers alike.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Our Values',
      details: [
        {
          title: 'Customer Obsessed',
          description:
            'We prioritize delivering unparalleled experiences to our customers, aligning with our mission to connect them to the finest products.',
        },
        {
          title: 'Innovation',
          description:
            'Every day, we harness our AI and massive data capabilities to innovate and enhance the ways we connect consumers with top-quality products.',
        },
        {
          title: 'Culture',
          description:
            'Our culture of learning, collaboration, transparency, experimentation, and passion drives us to continually improve and fulfill our mission.',
        },
        {
          title: 'Goal Driven',
          description:
            'Dedicated and goal-oriented, we strive for excellence in meeting our mission of connecting consumers with superior products.',
        },
        {
          title: 'Community',
          description:
            'We build and nurture a supportive community that thrives together, crucial for fulfilling our global mission.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'First Name', required: true },
      { id: 'last_name', label: 'Last Name', required: true },
      { id: 'email', label: 'Email Address', type: 'email', required: true },
      { id: 'phone', label: 'Phone Number', required: true },
      { id: 'full_address', label: 'Full Address (including country)', required: true },
      { id: 'desired_salary', label: 'Desired Salary', required: true },
      { id: 'start_date', label: 'Potential Start Date', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portfolio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Are you currently based in any of these countries:',
        name: 'country',
        options: [
          { label: 'Canada', value: 'CA' },
          { label: 'United States', value: 'US' },
          { label: 'None of the above', value: 'Other Countries' },
        ],
      },
    ],
    submit: 'Submit',
    requirements: 'Requirements',
    responsibilities: 'Responsibilities',
    uploadCV: 'Upload CV',
    viewMore: 'Show More',
    linkInput: {
      smartExtract: 'Smart extract:',
      apply: 'Apply',
      pasteHelper: 'Paste full URL for automatic username extraction',
      placeholderDefault: '---',
    },
  },

  // French
  fr: {
    header: {
      badge: 'CARRIÈRES',
      title: {
        part1: 'Rejoignez-nous',
        highlighted: 'Créer, Connecter, Innover et Aller Plus Loin.',
      },
      description:
        "Explorez des opportunités passionnantes pour rejoindre notre équipe innovante et contribuer à façonner l'avenir de la découverte de produits alimentée par l'IA.",
    },
    positions: {
      sectionBadge: 'Postes Ouverts',
      sectionTitle: {
        part1: 'Trouvez Votre',
        highlighted: 'Rôle Parfait',
      },
      sectionDescription:
        "Explorez des opportunités passionnantes pour rejoindre notre équipe innovante et contribuer à façonner l'avenir de la découverte de produits alimentée par l'IA.",
    },
    noPositions: {
      title: {
        part1: 'Aucun Poste Ouvert',
        highlighted: 'Pour le Moment',
      },
      description:
        'Nous recherchons toujours des personnes talentueuses. Revenez bientôt ou contactez-nous directement pour discuter des opportunités futures.',
      contactButton: 'Contactez-nous',
    },
    careers: 'Carrières',
    carrerFounderMessage: `Je reconnais qu'il est parfois difficile de juger un livre par sa couverture, c'est pourquoi on s'engage à examiner chaque candidature de manière équitable et individuelle. Si vous correspondez à notre équipe, nous vous contacterons rapidement. Si vous vous sentez fortement aligné avec notre équipe, veuillez nous contacter directement sur LinkedIn. Nous apprécions le temps que vous avez pris pour postuler.

    -Oli, Fondateur d'Inception`,
    careerIntroduction:
      "Notre mission chez Inception est d'aider les consommateurs du monde entier à trouver les meilleurs produits de leur région avec l'intelligence artificielle. Avec cet objectif en tête, nous faisons également de notre mieux pour aider les propriétaires de PME locales à améliorer leur présence en ligne.\n\nRejoignez-nous pour résoudre des problèmes du monde réel avec l'IA en aidant les entreprises locales et en travaillant sur des technologies exceptionnelles. Ensemble, nous pouvons avoir un impact significatif sur les communautés et les consommateurs.",
    careerMeta: {
      title: 'Carrière - Rejoignez-nous chez inceptionai.ca',
      description:
        "Rejoignez-nous pour résoudre des problèmes du monde réel avec l'IA en transformant les économies locales et en offrant des produits exceptionnels. Ensemble, nous pouvons avoir un impact significatif sur les communautés et les consommateurs.",
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Nos Valeurs',
      details: [
        {
          title: 'Culture',
          description: 'Apprentissage, collaboration, transparence, expérimentation et passion.',
        },
        {
          title: 'Orienté vers les Objectifs',
          description: 'Nous sommes dédiés et orientés vers les objectifs, garantissant des résultats de haute qualité.',
        },
        {
          title: 'Communauté',
          description: 'Nous prospérons ensemble avec notre communauté, favorisant un environnement de soutien.',
        },
        {
          title: 'Obsédé par le Client',
          description: `Notre approche est toujours de fournir des expériences client supérieures.`,
        },
        {
          title: 'Innovation',
          description: `Nous utilisons nos propres outils tous les jours pour innover et améliorer.`,
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Prénom', required: true },
      { id: 'last_name', label: 'Nom de famille', required: true },
      { id: 'email', label: 'Adresse e-mail', type: 'email', required: true },
      { id: 'phone', label: 'Numéro de téléphone', required: true },
      { id: 'full_address', label: 'Adresse complète (y compris le pays)', required: true },
      { id: 'desired_salary', label: 'Salaire souhaité', required: true },
      { id: 'start_date', label: 'Date de début potentielle', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portfolio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: "Êtes-vous actuellement basé dans l'un de ces pays :",
        name: 'country',
        options: [
          { label: 'Canada', value: 'CA' },
          { label: 'États-Unis', value: 'US' },
          { label: 'Aucun des pays ci-dessus', value: 'Autres Pays' },
        ],
      },
    ],
    submit: 'Soumettre',
    requirements: 'Exigences',
    responsibilities: 'Responsabilités',
    uploadCV: 'Télécharger CV',
    viewMore: 'Afficher plus',
    linkInput: {
      smartExtract: 'Extraction intelligente:',
      apply: 'Appliquer',
      pasteHelper: "Collez l'URL complète pour l'extraction automatique du nom d'utilisateur",
      placeholderDefault: '---',
    },
  },

  // Spanish
  es: {
    header: {
      badge: 'CARRERAS',
      title: {
        part1: 'Únete a Nosotros',
        highlighted: 'Crear, Conectar, Innovar e Ir Más Allá.',
      },
      description:
        'Explora oportunidades emocionantes para unirte a nuestro equipo innovador y ayudar a dar forma al futuro del descubrimiento de productos impulsado por IA.',
    },
    positions: {
      sectionBadge: 'Posiciones Abiertas',
      sectionTitle: {
        part1: 'Encuentra Tu',
        highlighted: 'Rol Perfecto',
      },
      sectionDescription:
        'Explora oportunidades emocionantes para unirte a nuestro equipo innovador y ayudar a dar forma al futuro del descubrimiento de productos impulsado por IA.',
    },
    noPositions: {
      title: {
        part1: 'No Hay Posiciones Abiertas',
        highlighted: 'Por Ahora',
      },
      description:
        'Siempre estamos buscando personas talentosas. Vuelve pronto o contáctanos directamente para discutir oportunidades futuras.',
      contactButton: 'Ponte en Contacto',
    },
    careers: 'Carreras',
    carrerFounderMessage: `Reconozco que a veces es difícil juzgar un libro por su portada, y estamos comprometidos a revisar cada solicitud de manera completa y justa. Si hay una posible coincidencia, nos pondremos en contacto con usted rápidamente. Si cree firmemente en su alineación con nuestro equipo, comuníquese directamente con nosotros en LinkedIn. Agradecemos el tiempo que ha dedicado a presentar su solicitud.
    
    -Oli, Fundador de Inception`,
    careerIntroduction: `Nuestra misión en Inception es ayudar a los consumidores de todo el mundo a encontrar los mejores productos en su región mediante inteligencia artificial. Con ese objetivo en mente, también hacemos nuestro mejor esfuerzo para ayudar a los propietarios de pequeñas empresas locales a mejorar su presencia en línea.

Únete a nosotros para resolver problemas reales con IA ayudando a negocios locales y trabajando en tecnologías de vanguardia. Juntos, podemos tener un impacto significativo tanto en las comunidades como en los consumidores.`,
    careerMeta: {
      title: 'Carrera - Únete a nosotros en inceptionai.ca',
      description:
        'Únete a nosotros para resolver problemas reales con IA transformando economías locales y ofreciendo productos excepcionales. Juntos, podemos tener un impacto significativo tanto en las comunidades como en los consumidores.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Nuestros valores',
      details: [
        {
          title: 'Enfoque en el cliente',
          description:
            'Priorizamos ofrecer experiencias inigualables a nuestros clientes, alineados con nuestra misión de conectarlos con los mejores productos.',
        },
        {
          title: 'Innovación',
          description:
            'Cada día aprovechamos nuestras capacidades de IA y enormes cantidades de datos para innovar y mejorar la forma en que conectamos a los consumidores con productos de alta calidad.',
        },
        {
          title: 'Cultura',
          description:
            'Nuestra cultura de aprendizaje, colaboración, transparencia, experimentación y pasión nos impulsa a mejorar continuamente y cumplir nuestra misión.',
        },
        {
          title: 'Orientados a objetivos',
          description:
            'Dedicados y orientados a objetivos, buscamos la excelencia en cumplir nuestra misión de conectar a los consumidores con productos superiores.',
        },
        {
          title: 'Comunidad',
          description:
            'Construimos y fomentamos una comunidad de apoyo que prospera unida, crucial para cumplir nuestra misión global.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Nombre', required: true },
      { id: 'last_name', label: 'Apellido', required: true },
      { id: 'email', label: 'Correo electrónico', type: 'email', required: true },
      { id: 'phone', label: 'Número de teléfono', required: true },
      { id: 'full_address', label: 'Dirección completa (incluyendo país)', required: true },
      { id: 'desired_salary', label: 'Salario deseado', required: true },
      { id: 'start_date', label: 'Fecha de inicio potencial', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portafolio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: '¿Actualmente resides en alguno de estos países?',
        name: 'country',
        options: [
          { label: 'Canadá', value: 'CA' },
          { label: 'Estados Unidos', value: 'US' },
          { label: 'Ninguno de los anteriores', value: 'Otros Países' },
        ],
      },
    ],
    submit: 'Enviar',
    requirements: 'Requisitos',
    responsibilities: 'Responsabilidades',
    uploadCV: 'Subir CV',
    viewMore: 'Mostrar más',
    linkInput: {
      smartExtract: 'Extracción inteligente:',
      apply: 'Aplicar',
      pasteHelper: 'Pega la URL completa para la extracción automática del nombre de usuario',
      placeholderDefault: '---',
    },
  },

  // German
  de: {
    header: {
      badge: 'KARRIERE',
      title: {
        part1: 'Treten Sie Bei',
        highlighted: 'Erstellen, Verbinden, Innovieren und Darüber Hinausgehen.',
      },
      description:
        'Entdecken Sie aufregende Möglichkeiten, unserem innovativen Team beizutreten und die Zukunft der KI-gestützten Produktentdeckung mitzugestalten.',
    },
    positions: {
      sectionBadge: 'Offene Stellen',
      sectionTitle: {
        part1: 'Finden Sie Ihre',
        highlighted: 'Perfekte Rolle',
      },
      sectionDescription:
        'Entdecken Sie aufregende Möglichkeiten, unserem innovativen Team beizutreten und die Zukunft der KI-gestützten Produktentdeckung mitzugestalten.',
    },
    noPositions: {
      title: {
        part1: 'Keine Offenen Stellen',
        highlighted: 'Im Moment',
      },
      description:
        'Wir suchen immer nach talentierten Personen. Schauen Sie bald wieder vorbei oder kontaktieren Sie uns direkt, um zukünftige Möglichkeiten zu besprechen.',
      contactButton: 'Kontakt Aufnehmen',
    },
    careers: 'Karriere',
    carrerFounderMessage: `Ich erkenne an, dass es manchmal schwierig ist, ein Buch nach seinem Einband zu beurteilen, und wir verpflichten uns, jede Bewerbung gründlich und fair zu prüfen. Wenn es eine potenzielle Übereinstimmung gibt, werden wir Sie umgehend kontaktieren. Wenn Sie sich stark mit unserem Team verbunden fühlen, kontaktieren Sie uns bitte direkt auf LinkedIn. Wir schätzen die Zeit, die Sie sich genommen haben, um sich zu bewerben.

    -Oli, Gründer von Inception`,
    careerIntroduction: `Unsere Mission bei Inception ist es, Verbrauchern weltweit mithilfe von künstlicher Intelligenz die besten Produkte in ihrer Region zu finden. Mit diesem Ziel vor Augen geben wir auch unser Bestes, um lokalen Kleinunternehmern zu einer verbesserten Online-Präsenz zu verhelfen.

Schließe dich uns an, um reale Probleme mit KI zu lösen, indem du lokalen Unternehmen hilfst und an herausragenden Technologien arbeitest. Gemeinsam können wir sowohl in Gemeinden als auch bei Verbrauchern einen bedeutenden Beitrag leisten.`,
    careerMeta: {
      title: 'Karriere - Mach mit bei inceptionai.ca',
      description:
        'Schließe dich uns an, um reale Probleme mit KI zu lösen, lokale Wirtschaften zu transformieren und herausragende Produkte zu liefern. Gemeinsam können wir sowohl in Gemeinden als auch bei Verbrauchern einen bedeutenden Beitrag leisten.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Unsere Werte',
      details: [
        {
          title: 'Kundenfokus',
          description:
            'Wir priorisieren es, unseren Kunden unvergleichliche Erlebnisse zu bieten, im Einklang mit unserer Mission, sie mit den besten Produkten zu verbinden.',
        },
        {
          title: 'Innovation',
          description:
            'Jeden Tag nutzen wir unsere KI- und umfangreichen Datenfähigkeiten, um Innovationen voranzutreiben und die Art und Weise zu verbessern, wie wir Verbraucher mit hochwertigen Produkten verbinden.',
        },
        {
          title: 'Kultur',
          description:
            'Unsere Kultur des Lernens, der Zusammenarbeit, Transparenz, Experimentierens und Leidenschaft treibt uns an, uns kontinuierlich zu verbessern und unsere Mission zu erfüllen.',
        },
        {
          title: 'Zielorientierung',
          description:
            'Engagiert und zielorientiert streben wir nach Exzellenz in der Erreichung unserer Mission, Verbraucher mit erstklassigen Produkten zu verbinden.',
        },
        {
          title: 'Gemeinschaft',
          description:
            'Wir bauen eine unterstützende Gemeinschaft auf und pflegen sie, die gemeinsam gedeiht, was entscheidend für die Erfüllung unserer globalen Mission ist.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Vorname', required: true },
      { id: 'last_name', label: 'Nachname', required: true },
      { id: 'email', label: 'E-Mail-Adresse', type: 'email', required: true },
      { id: 'phone', label: 'Telefonnummer', required: true },
      { id: 'full_address', label: 'Vollständige Adresse (einschließlich Land)', required: true },
      { id: 'desired_salary', label: 'Gewünschtes Gehalt', required: true },
      { id: 'start_date', label: 'Potenzielle Startdatum', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portfolio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Bist du derzeit in einem dieser Länder ansässig?',
        name: 'country',
        options: [
          { label: 'Kanada', value: 'CA' },
          { label: 'Vereinigte Staaten', value: 'US' },
          { label: 'Keines der oben genannten', value: 'Andere Länder' },
        ],
      },
    ],
    submit: 'Einreichen',
    requirements: 'Anforderungen',
    responsibilities: 'Verantwortlichkeiten',
    uploadCV: 'Lebenslauf hochladen',
    viewMore: 'Mehr anzeigen',
    linkInput: {
      smartExtract: 'Intelligente Extraktion:',
      apply: 'Anwenden',
      pasteHelper: 'Vollständige URL für automatische Benutzernamen-Extraktion einfügen',
      placeholderDefault: '---',
    },
  },
  // Italian
  it: {
    header: {
      badge: 'CARRIERE',
      title: {
        part1: 'Unisciti a Noi',
        highlighted: 'Crea, Connetti, Innova e Vai Oltre.',
      },
      description:
        'Esplora opportunità entusiasmanti per unirti al nostro team innovativo e contribuire a plasmare il futuro della scoperta di prodotti basata sull\'IA.',
    },
    positions: {
      sectionBadge: 'Posizioni Aperte',
      sectionTitle: {
        part1: 'Trova Il Tuo',
        highlighted: 'Ruolo Perfetto',
      },
      sectionDescription:
        'Esplora opportunità entusiasmanti per unirti al nostro team innovativo e contribuire a plasmare il futuro della scoperta di prodotti basata sull\'IA.',
    },
    noPositions: {
      title: {
        part1: 'Nessuna Posizione Aperta',
        highlighted: 'Al Momento',
      },
      description:
        'Siamo sempre alla ricerca di persone di talento. Torna presto o contattaci direttamente per discutere le opportunità future.',
      contactButton: 'Mettiti in Contatto',
    },
    careers: 'Carriere',
    carrerFounderMessage: `Riconosco che a volte è difficile giudicare un libro dalla copertina, e ci impegniamo a esaminare ogni candidatura in modo approfondito e imparziale. Se c'è una potenziale corrispondenza, ti contatteremo prontamente. Se senti fortemente di allinearti con il nostro team, contattaci direttamente su LinkedIn. Apprezziamo il tempo che hai dedicato alla candidatura.

    -Oli, Fondatore di Inception`,
    careerIntroduction:
      "La nostra missione in Inception è aiutare i consumatori di tutto il mondo a trovare i migliori prodotti nella loro regione con l'intelligenza artificiale. Con questo obiettivo in mente, facciamo anche del nostro meglio per aiutare i proprietari di piccole imprese locali a migliorare la loro presenza online.\n\nUnisciti a noi per risolvere problemi reali con l'IA aiutando le aziende locali e lavorando su tecnologie straordinarie. Insieme, possiamo avere un impatto significativo sia sulle comunità che sui consumatori.",
    careerMeta: {
      title: 'Carriera - Unisciti a Noi in inceptionai.ca',
      description:
        'Unisciti a noi per risolvere problemi reali con l\'IA trasformando le economie locali e offrendo prodotti straordinari. Insieme, possiamo avere un impatto significativo sia sulle comunità che sui consumatori.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'I Nostri Valori',
      details: [
        {
          title: 'Ossessionati dai Clienti',
          description:
            'Diamo priorità alla fornitura di esperienze impareggiabili ai nostri clienti, in linea con la nostra missione di connetterli ai migliori prodotti.',
        },
        {
          title: 'Innovazione',
          description:
            'Ogni giorno sfruttiamo le nostre capacità di IA e di dati per innovare e migliorare i modi in cui connettiamo i consumatori con prodotti di alta qualità.',
        },
        {
          title: 'Cultura',
          description:
            'La nostra cultura di apprendimento, collaborazione, trasparenza, sperimentazione e passione ci spinge a migliorare continuamente e a realizzare la nostra missione.',
        },
        {
          title: 'Orientati agli Obiettivi',
          description:
            'Dedicati e orientati agli obiettivi, puntiamo all\'eccellenza nel realizzare la nostra missione di connettere i consumatori con prodotti superiori.',
        },
        {
          title: 'Comunità',
          description:
            'Costruiamo e coltiviamo una comunità di supporto che prospera insieme, fondamentale per realizzare la nostra missione globale.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Nome', required: true },
      { id: 'last_name', label: 'Cognome', required: true },
      { id: 'email', label: 'Indirizzo email', type: 'email', required: true },
      { id: 'phone', label: 'Numero di telefono', required: true },
      { id: 'full_address', label: 'Indirizzo completo (incluso paese)', required: true },
      { id: 'desired_salary', label: 'Stipendio desiderato', required: true },
      { id: 'start_date', label: 'Data di inizio potenziale', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portfolio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Sei attualmente residente in uno di questi paesi:',
        name: 'country',
        options: [
          { label: 'Canada', value: 'CA' },
          { label: 'Stati Uniti', value: 'US' },
          { label: 'Nessuno dei precedenti', value: 'Altri Paesi' },
        ],
      },
    ],
    submit: 'Invia',
    requirements: 'Requisiti',
    responsibilities: 'Responsabilità',
    uploadCV: 'Carica CV',
    viewMore: 'Mostra altro',
    linkInput: {
      smartExtract: 'Estrazione intelligente:',
      apply: 'Applica',
      pasteHelper: 'Incolla l\'URL completo per l\'estrazione automatica del nome utente',
      placeholderDefault: '---',
    },
  },

  // Brazilian Portuguese
  'pt-BR': {
    header: {
      badge: 'CARREIRAS',
      title: {
        part1: 'Junte-se a Nós',
        highlighted: 'Crie, Conecte, Inove e Vá Além.',
      },
      description:
        'Explore oportunidades incríveis para se juntar ao nosso time inovador e ajudar a moldar o futuro da descoberta de produtos com IA.',
    },
    positions: {
      sectionBadge: 'Vagas Abertas',
      sectionTitle: {
        part1: 'Encontre o Seu',
        highlighted: 'Papel Perfeito',
      },
      sectionDescription:
        'Explore oportunidades incríveis para se juntar ao nosso time inovador e ajudar a moldar o futuro da descoberta de produtos com IA.',
    },
    noPositions: {
      title: {
        part1: 'Nenhuma Vaga Aberta',
        highlighted: 'No Momento',
      },
      description:
        'Estamos sempre procurando pessoas talentosas. Volte em breve ou entre em contato conosco diretamente para discutir oportunidades futuras.',
      contactButton: 'Entre em Contato',
    },
    careers: 'Carreiras',
    carrerFounderMessage: `Reconheço que às vezes é difícil julgar um livro pela capa, e estamos comprometidos em analisar cada candidatura de forma completa e justa. Se houver uma possível compatibilidade, entraremos em contato com você prontamente. Se você se sente fortemente alinhado com o nosso time, entre em contato conosco diretamente no LinkedIn. Agradecemos o tempo que você dedicou para se candidatar.
    
    -Oli, Fundador da Inception`,
    careerIntroduction:
      "Nossa missão na Inception é ajudar consumidores de todo o mundo a encontrar os melhores produtos em sua região com inteligência artificial. Com esse objetivo em mente, também fazemos nosso melhor para ajudar os proprietários de pequenas empresas locais a melhorar sua presença online.\n\nJunte-se a nós para resolver problemas reais com IA ajudando empresas locais e trabalhando em tecnologias extraordinárias. Juntos, podemos causar um impacto significativo tanto nas comunidades quanto nos consumidores.",
    careerMeta: {
      title: 'Carreira - Junte-se a Nós na inceptionai.ca',
      description:
        'Junte-se a nós para resolver problemas reais com IA transformando economias locais e entregando produtos extraordinários. Juntos, podemos causar um impacto significativo tanto nas comunidades quanto nos consumidores.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Nossos Valores',
      details: [
        {
          title: 'Obcecados pelo Cliente',
          description:
            'Priorizamos oferecer experiências incomparáveis aos nossos clientes, alinhados com nossa missão de conectá-los aos melhores produtos.',
        },
        {
          title: 'Inovação',
          description:
            'Todo dia aproveitamos nossas capacidades de IA e de dados para inovar e melhorar as formas como conectamos consumidores com produtos de alta qualidade.',
        },
        {
          title: 'Cultura',
          description:
            'Nossa cultura de aprendizado, colaboração, transparência, experimentação e paixão nos impulsiona a melhorar continuamente e cumprir nossa missão.',
        },
        {
          title: 'Orientados a Objetivos',
          description:
            'Dedicados e orientados a objetivos, buscamos a excelência no cumprimento de nossa missão de conectar consumidores com produtos superiores.',
        },
        {
          title: 'Comunidade',
          description:
            'Construímos e cultivamos uma comunidade de apoio que prospera em conjunto, essencial para cumprir nossa missão global.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Nome', required: true },
      { id: 'last_name', label: 'Sobrenome', required: true },
      { id: 'email', label: 'Endereço de email', type: 'email', required: true },
      { id: 'phone', label: 'Número de telefone', required: true },
      { id: 'full_address', label: 'Endereço completo (incluindo país)', required: true },
      { id: 'desired_salary', label: 'Salário desejado', required: true },
      { id: 'start_date', label: 'Data de início potencial', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portfólio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Você está atualmente em algum desses países:',
        name: 'country',
        options: [
          { label: 'Canadá', value: 'CA' },
          { label: 'Estados Unidos', value: 'US' },
          { label: 'Nenhum dos anteriores', value: 'Outros Países' },
        ],
      },
    ],
    submit: 'Enviar',
    requirements: 'Requisitos',
    responsibilities: 'Responsabilidades',
    uploadCV: 'Enviar CV',
    viewMore: 'Mostrar mais',
    linkInput: {
      smartExtract: 'Extração inteligente:',
      apply: 'Aplicar',
      pasteHelper: 'Cole a URL completa para extração automática do nome de usuário',
      placeholderDefault: '---',
    },
  },

  // European Portuguese
  'pt-PT': {
    header: {
      badge: 'CARREIRAS',
      title: {
        part1: 'Junte-se a Nós',
        highlighted: 'Crie, Conecte, Inove e Vá Além.',
      },
      description:
        'Explore oportunidades emocionantes para se juntar à nossa equipa inovadora e ajudar a moldar o futuro da descoberta de produtos com IA.',
    },
    positions: {
      sectionBadge: 'Vagas Abertas',
      sectionTitle: {
        part1: 'Encontre o Seu',
        highlighted: 'Papel Perfeito',
      },
      sectionDescription:
        'Explore oportunidades emocionantes para se juntar à nossa equipa inovadora e ajudar a moldar o futuro da descoberta de produtos com IA.',
    },
    noPositions: {
      title: {
        part1: 'Sem Vagas Abertas',
        highlighted: 'De Momento',
      },
      description:
        'Estamos sempre à procura de pessoas talentosas. Volte em breve ou contacte-nos diretamente para discutir oportunidades futuras.',
      contactButton: 'Entre em Contacto',
    },
    careers: 'Carreiras',
    carrerFounderMessage: `Reconheço que por vezes é difícil julgar um livro pela capa, e estamos comprometidos em analisar cada candidatura de forma completa e justa. Se houver uma possível compatibilidade, entraremos em contacto consigo prontamente. Se se sentir fortemente alinhado com a nossa equipa, contacte-nos diretamente no LinkedIn. Agradecemos o tempo que dedicou à candidatura.
    
    -Oli, Fundador da Inception`,
    careerIntroduction:
      "A nossa missão na Inception é ajudar consumidores de todo o mundo a encontrar os melhores produtos na sua região com inteligência artificial. Com esse objetivo em mente, também fazemos o nosso melhor para ajudar os proprietários de pequenas empresas locais a melhorar a sua presença online.\n\nJunte-se a nós para resolver problemas reais com IA ajudando empresas locais e trabalhando em tecnologias extraordinárias. Juntos, podemos causar um impacto significativo tanto nas comunidades como nos consumidores.",
    careerMeta: {
      title: 'Carreira - Junte-se a Nós na inceptionai.ca',
      description:
        'Junte-se a nós para resolver problemas reais com IA transformando economias locais e entregando produtos extraordinários. Juntos, podemos causar um impacto significativo tanto nas comunidades como nos consumidores.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Os Nossos Valores',
      details: [
        {
          title: 'Obcecados pelo Cliente',
          description:
            'Priorizamos oferecer experiências incomparáveis aos nossos clientes, alinhados com a nossa missão de os ligar aos melhores produtos.',
        },
        {
          title: 'Inovação',
          description:
            'Todos os dias aproveitamos as nossas capacidades de IA e de dados para inovar e melhorar as formas como ligamos consumidores a produtos de alta qualidade.',
        },
        {
          title: 'Cultura',
          description:
            'A nossa cultura de aprendizagem, colaboração, transparência, experimentação e paixão impulsiona-nos a melhorar continuamente e a cumprir a nossa missão.',
        },
        {
          title: 'Orientados para Objetivos',
          description:
            'Dedicados e orientados para objetivos, procuramos a excelência no cumprimento da nossa missão de ligar consumidores a produtos superiores.',
        },
        {
          title: 'Comunidade',
          description:
            'Construímos e cultivamos uma comunidade de apoio que prospera em conjunto, essencial para cumprir a nossa missão global.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Primeiro nome', required: true },
      { id: 'last_name', label: 'Apelido', required: true },
      { id: 'email', label: 'Endereço de email', type: 'email', required: true },
      { id: 'phone', label: 'Número de telefone', required: true },
      { id: 'full_address', label: 'Morada completa (incluindo país)', required: true },
      { id: 'desired_salary', label: 'Salário pretendido', required: true },
      { id: 'start_date', label: 'Data de início potencial', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portfólio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Está atualmente em algum destes países:',
        name: 'country',
        options: [
          { label: 'Canadá', value: 'CA' },
          { label: 'Estados Unidos', value: 'US' },
          { label: 'Nenhum dos anteriores', value: 'Outros Países' },
        ],
      },
    ],
    submit: 'Submeter',
    requirements: 'Requisitos',
    responsibilities: 'Responsabilidades',
    uploadCV: 'Carregar CV',
    viewMore: 'Mostrar mais',
    linkInput: {
      smartExtract: 'Extracção inteligente:',
      apply: 'Aplicar',
      pasteHelper: 'Cole o URL completo para extracção automática do nome de utilizador',
      placeholderDefault: '---',
    },
  },

  // Tamil
  ta: {
    header: {
      badge: 'வேலைவாய்ப்புகள்',
      title: {
        part1: 'எங்களுடன் சேருங்கள்',
        highlighted: 'உருவாக்குங்கள், இணையுங்கள், புதுமை செய்யுங்கள், எல்லையைக் கடவுங்கள்.',
      },
      description:
        'AI-இயக்கப்படும் பொருள் கண்டுபிடிப்பின் எதிர்காலத்தை வடிவமைக்க உதவும் எங்கள் புதுமையான குழுவில் சேர அற்புதமான வாய்ப்புகளை ஆராயுங்கள்.',
    },
    positions: {
      sectionBadge: 'காலிப் பணியிடங்கள்',
      sectionTitle: {
        part1: 'உங்களுக்கான',
        highlighted: 'சரியான பதவியைக் கண்டறியுங்கள்',
      },
      sectionDescription:
        'AI-இயக்கப்படும் பொருள் கண்டுபிடிப்பின் எதிர்காலத்தை வடிவமைக்க உதவும் எங்கள் புதுமையான குழுவில் சேர அற்புதமான வாய்ப்புகளை ஆராயுங்கள்.',
    },
    noPositions: {
      title: {
        part1: 'தற்போது காலிப் பணியிடங்கள்',
        highlighted: 'இல்லை',
      },
      description:
        'திறமையான நபர்களை நாங்கள் எப்போதும் தேடிக்கொண்டிருக்கிறோம். விரைவில் மீண்டும் வாருங்கள் அல்லது எதிர்கால வாய்ப்புகளைப் பற்றி நேரடியாக எங்களைத் தொடர்புகொள்ளுங்கள்.',
      contactButton: 'தொடர்புகொள்ளுங்கள்',
    },
    careers: 'வேலைவாய்ப்புகள்',
    carrerFounderMessage: `புத்தகத்தை அட்டையை வைத்து மதிப்பிடுவது சில நேரங்களில் கடினம் என்பதை நான் புரிந்துகொள்கிறேன், ஒவ்வொரு விண்ணப்பத்தையும் முழுமையாகவும் நியாயமாகவும் மதிப்பாய்வு செய்ய நாங்கள் உறுதிபூண்டுள்ளோம். சாத்தியமான பொருத்தம் இருந்தால், உடனடியாக உங்களைத் தொடர்புகொள்வோம். எங்கள் குழுவுடன் உங்கள் பொருத்தம் குறித்து நீங்கள் உறுதியாக நம்பினால், LinkedIn-ல் நேரடியாக எங்களைத் தொடர்புகொள்ளுங்கள். விண்ணப்பிக்க நீங்கள் எடுத்த நேரத்தை நாங்கள் பாராட்டுகிறோம்.
    
    -Oli, Inception நிறுவனர்`,
    careerIntroduction:
      "Inception-ல் எங்கள் நோக்கம், செயற்கை நுண்ணறிவின் உதவியுடன் உலகம் முழுவதும் உள்ள நுகர்வோர் தங்கள் பகுதியில் சிறந்த பொருட்களைக் கண்டறிய உதவுவதாகும். அந்த இலக்கை மனதில் கொண்டு, உள்ளூர் சிறு வணிக உரிமையாளர்களின் ஆன்லைன் இருப்பை மேம்படுத்தவும் நாங்கள் எங்கள் சிறந்ததைச் செய்கிறோம்.\n\nஉள்ளூர் வணிகங்களுக்கு உதவியும் சிறந்த தொழில்நுட்பங்களில் பணிபுரிந்தும் AI மூலம் நிஜ உலகப் பிரச்சனைகளைத் தீர்க்க எங்களுடன் சேருங்கள். ஒன்றாக, சமூகங்கள் மற்றும் நுகர்வோர் இருவருக்கும் அர்த்தமுள்ள தாக்கத்தை ஏற்படுத்தலாம்.",
    careerMeta: {
      title: 'வேலைவாய்ப்பு - inceptionai.ca-வில் எங்களுடன் சேருங்கள்',
      description:
        'AI மூலம் நிஜ உலகப் பிரச்சனைகளைத் தீர்க்க, உள்ளூர் பொருளாதாரங்களை மாற்றி, சிறந்த பொருட்களை வழங்க எங்களுடன் சேருங்கள். ஒன்றாக, சமூகங்கள் மற்றும் நுகர்வோர் இருவருக்கும் அர்த்தமுள்ள தாக்கத்தை ஏற்படுத்தலாம்.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'எங்கள் மதிப்புகள்',
      details: [
        {
          title: 'வாடிக்கையாளர் மையம்',
          description:
            'சிறந்த பொருட்களுடன் வாடிக்கையாளர்களை இணைக்கும் எங்கள் நோக்கத்துடன் ஒத்திசைந்து, ஒப்பற்ற அனுபவங்களை வழங்குவதற்கு முன்னுரிமை அளிக்கிறோம்.',
        },
        {
          title: 'புதுமை',
          description:
            'ஒவ்வொரு நாளும், உயர்தர பொருட்களுடன் நுகர்வோரை இணைக்கும் வழிகளை புதுமைப்படுத்தவும் மேம்படுத்தவும் எங்கள் AI மற்றும் பாரிய தரவு திறன்களைப் பயன்படுத்துகிறோம்.',
        },
        {
          title: 'கலாசாரம்',
          description:
            'கற்றல், ஒத்துழைப்பு, வெளிப்படைத்தன்மை, பரிசோதனை மற்றும் ஆர்வம் ஆகியவற்றின் எங்கள் கலாசாரம் தொடர்ந்து மேம்படவும் எங்கள் நோக்கத்தை நிறைவேற்றவும் எங்களைத் தூண்டுகிறது.',
        },
        {
          title: 'இலக்கு சார்ந்தது',
          description:
            'அர்ப்பணிப்புடனும் இலக்கு நோக்கியும், உயர்தரப் பொருட்களுடன் நுகர்வோரை இணைக்கும் எங்கள் நோக்கத்தில் சிறந்து விளங்குகிறோம்.',
        },
        {
          title: 'சமூகம்',
          description:
            'எங்கள் உலகளாவிய நோக்கத்தை நிறைவேற்ற முக்கியமான, ஒன்றாக செழிக்கும் ஆதரவான சமூகத்தை உருவாக்கி வளர்க்கிறோம்.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'முதல் பெயர்', required: true },
      { id: 'last_name', label: 'கடைசிப் பெயர்', required: true },
      { id: 'email', label: 'மின்னஞ்சல் முகவரி', type: 'email', required: true },
      { id: 'phone', label: 'தொலைபேசி எண்', required: true },
      { id: 'full_address', label: 'முழு முகவரி (நாடு உட்பட)', required: true },
      { id: 'desired_salary', label: 'எதிர்பார்க்கும் சம்பளம்', required: true },
      { id: 'start_date', label: 'சாத்தியமான தொடக்க தேதி', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'போர்ட்ஃபோலியோ', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'நீங்கள் தற்போது இந்த நாடுகளில் ஏதேனும் ஒன்றில் வசிக்கிறீர்களா:',
        name: 'country',
        options: [
          { label: 'கனடா', value: 'CA' },
          { label: 'அமெரிக்கா', value: 'US' },
          { label: 'மேலே உள்ளவை எதுவும் இல்லை', value: 'Other Countries' },
        ],
      },
    ],
    submit: 'சமர்ப்பிக்கவும்',
    requirements: 'தேவைகள்',
    responsibilities: 'பொறுப்புகள்',
    uploadCV: 'CV பதிவேற்றவும்',
    viewMore: 'மேலும் காட்டு',
    linkInput: {
      smartExtract: 'புத்திசாலி பிரித்தெடுப்பு:',
      apply: 'பயன்படுத்து',
      pasteHelper: 'பயனர்பெயரை தானாகப் பிரித்தெடுக்க முழு URL-ஐ ஒட்டவும்',
      placeholderDefault: '---',
    },
  },

  sv: {
    header: {
      badge: 'KARRIÄR',
      title: {
        part1: 'Gå med oss',
        highlighted: 'Skapa, anslut, förnya och gå bortom.',
      },
      description:
        'Utforska spännande möjligheter att gå med i vårt innovativa team och hjälpa till att forma framtiden för AI-driven produktupptäckt.',
    },
    positions: {
      sectionBadge: 'Lediga tjänster',
      sectionTitle: {
        part1: 'Hitta din',
        highlighted: 'Perfekta roll',
      },
      sectionDescription:
        'Utforska spännande möjligheter att gå med i vårt innovativa team och hjälpa till att forma framtiden för AI-driven produktupptäckt.',
    },
    noPositions: {
      title: {
        part1: 'Inga lediga tjänster',
        highlighted: 'Just nu',
      },
      description:
        'Vi letar alltid efter begåvade personer. Kom tillbaka snart eller kontakta oss direkt för att diskutera framtida möjligheter.',
      contactButton: 'Hör av dig',
    },
    careers: 'Karriär',
    carrerFounderMessage: `Jag förstår att det ibland är svårt att bedöma en bok efter omslaget, och vi åtar oss att granska varje ansökan noggrant och rättvist. Om det finns en potentiell matchning kontaktar vi dig snabbt. Om du känner starkt för att du passar i vårt team, kontakta oss direkt på LinkedIn. Vi uppskattar tiden du lagt ner på att söka.

    -Oli, grundare av Inception`,
    careerIntroduction:
      "Vårt uppdrag på Inception är att hjälpa konsumenter världen över att hitta de bästa produkterna i sin region med artificiell intelligens. Med det målet i åtanke gör vi även vårt bästa för att hjälpa lokala småföretagare att förbättra sin närvaro online.\n\nGå med oss i att lösa verkliga problem med AI genom att hjälpa lokala företag och arbeta med enastående teknik. Tillsammans kan vi göra en meningsfull skillnad för både samhällen och konsumenter.",
    careerMeta: {
      title: 'Karriär – Gå med oss på inceptionai.ca',
      description:
        'Gå med oss i att lösa verkliga problem med AI genom att omvandla lokala ekonomier och leverera enastående produkter. Tillsammans kan vi göra en meningsfull skillnad för både samhällen och konsumenter.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Våra värderingar',
      details: [
        {
          title: 'Kundfokuserade',
          description:
            'Vi prioriterar att leverera oöverträffade upplevelser till våra kunder, i linje med vårt uppdrag att koppla dem till de bästa produkterna.',
        },
        {
          title: 'Innovation',
          description:
            'Varje dag utnyttjar vi våra AI- och datakapaciteter för att förnya och förbättra hur vi kopplar konsumenter med högkvalitativa produkter.',
        },
        {
          title: 'Kultur',
          description:
            'Vår kultur av lärande, samarbete, transparens, experimenterande och passion driver oss att ständigt förbättras och uppfylla vårt uppdrag.',
        },
        {
          title: 'Målinriktade',
          description:
            'Engagerade och målinriktade strävar vi efter excellens i att uppfylla vårt uppdrag att koppla konsumenter med överlägsna produkter.',
        },
        {
          title: 'Gemenskap',
          description:
            'Vi bygger och vårdar en stödjande gemenskap som blomstrar tillsammans, avgörande för att uppfylla vårt globala uppdrag.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Förnamn', required: true },
      { id: 'last_name', label: 'Efternamn', required: true },
      { id: 'email', label: 'E-postadress', type: 'email', required: true },
      { id: 'phone', label: 'Telefonnummer', required: true },
      { id: 'full_address', label: 'Fullständig adress (inklusive land)', required: true },
      { id: 'desired_salary', label: 'Önskad lön', required: true },
      { id: 'start_date', label: 'Möjligt startdatum', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portfolio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Bor du för närvarande i något av dessa länder:',
        name: 'country',
        options: [
          { label: 'Kanada', value: 'CA' },
          { label: 'USA', value: 'US' },
          { label: 'Inget av ovanstående', value: 'Andra länder' },
        ],
      },
    ],
    submit: 'Skicka',
    requirements: 'Krav',
    responsibilities: 'Ansvarsområden',
    uploadCV: 'Ladda upp CV',
    viewMore: 'Visa mer',
    linkInput: {
      smartExtract: 'Smart extrahering:',
      apply: 'Tillämpa',
      pasteHelper: 'Klistra in fullständig URL för automatisk extrahering av användarnamn',
      placeholderDefault: '---',
    },
  },

  // Scottish Gaelic
  gd: {
    header: {
      badge: 'DREUCHDAN',
      title: {
        part1: 'Thig Còmhla Rinn',
        highlighted: 'Cruthaich, Ceangail, Ùr-ghnàthaich, agus Rach Nas Fhaide.',
      },
      description:
        'Rannsaich cothroman brosnachail airson tighinn còmhla ri ar sgioba ùr-ghnàthach agus cuideachadh le bhith a\' cumadh àm ri teachd lorg bathair le cumhachd AI.',
    },
    positions: {
      sectionBadge: 'Dreuchdan Fosgailte',
      sectionTitle: {
        part1: 'Lorg Do',
        highlighted: 'Dhreuchd Fhoirfe',
      },
      sectionDescription:
        'Rannsaich cothroman brosnachail airson tighinn còmhla ri ar sgioba ùr-ghnàthach agus cuideachadh le bhith a\' cumadh àm ri teachd lorg bathair le cumhachd AI.',
    },
    noPositions: {
      title: {
        part1: 'Gun Dreuchdan Fosgailte',
        highlighted: 'An-dràsta',
      },
      description:
        'Tha sinn an-còmhnaidh a\' lorg dhaoine tàlantach. Thig air ais a dh\'aithghearr no cuir fios thugainn gu dìreach gus cothroman san àm ri teachd a dheasbad.',
      contactButton: 'Cuir Fios',
    },
    careers: 'Dreuchdan',
    carrerFounderMessage: `Tha mi a\' tuigsinn gu bheil e uaireannan doirbh leabhar a mheasadh bhon chòmhdach aige, agus tha sinn dealasach gach tagradh a sgrùdadh gu mionaideach agus gu cothromach. Ma tha co-fhreagarrachd ann, cuiridh sinn fios thugad gu sgiobalta. Ma tha thu a\' faireachdainn gu làidir mu do cho-thaobhadh leis an sgioba againn, cuir fios thugainn gu dìreach air LinkedIn. Tha sinn a\' cur luach air an ùine a ghabh thu gus tagradh a chur a-steach.

    -Oli, Stèidheadair Inception`,
    careerIntroduction:
      'Is e ar misean aig Inception cuideachadh le luchd-cleachdaidh air feadh an t-saoghail na batharan as fheàrr nan sgìre a lorg le inntleachd fuadain. Leis an amas sin, tha sinn cuideachd a\' dèanamh ar dìcheall gus cuideachadh le luchd-seilbh ghnothachasan beaga ionadail am preusantachd air-loidhne a leasachadh.\n\nThig còmhla rinn gus duilgheadasan fìor a rèiteachadh le AI le bhith a\' cuideachadh ghnothachasan ionadail agus ag obair air teicneòlasan sònraichte. Còmhla, is urrainn dhuinn buaidh chudromach a thoirt air coimhearsnachdan agus luchd-cleachdaidh.',
    careerMeta: {
      title: 'Dreuchd - Thig Còmhla Rinn aig inceptionai.ca',
      description:
        'Thig còmhla rinn gus duilgheadasan fìor a rèiteachadh le AI le bhith ag atharrachadh eaconamaidhean ionadail agus a\' lìbhrigeadh batharan air leth. Còmhla, is urrainn dhuinn buaidh chudromach a thoirt air coimhearsnachdan agus luchd-cleachdaidh.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Ar Luachan',
      details: [
        {
          title: 'Dian-dhealas don Luchd-ceannach',
          description:
            'Tha sinn a\' cur prìomhachas air eòlasan gun choimeas a lìbhrigeadh dha ar luchd-ceannach, a rèir ar misean gus an ceangal ris na batharan as fheàrr.',
        },
        {
          title: 'Ùr-ghnàthachadh',
          description:
            'Gach latha, tha sinn a\' cleachdadh ar comasan AI agus dàta mòr gus ùr-ghnàthachadh agus na dòighean anns a bheil sinn a\' ceangal luchd-cleachdaidh ri batharan àrd-chàileachd a leasachadh.',
        },
        {
          title: 'Cultar',
          description:
            'Tha ar cultar ionnsachaidh, co-obrachaidh, follaiseachd, deuchainneil agus dealas gar brosnachadh gus a bhith an-còmhnaidh a\' leasachadh agus ar misean a choileanadh.',
        },
        {
          title: 'Air a Stiùireadh le Amasan',
          description:
            'Dealasach agus stiùirichte le amasan, tha sinn a\' strì airson sàr-mhathais ann a bhith a\' coileanadh ar misean gus luchd-cleachdaidh a cheangal ri batharan as fheàrr.',
        },
        {
          title: 'Coimhearsnachd',
          description:
            'Bidh sinn a\' togail agus a\' togail coimhearsnachd thaic a tha a\' soirbheachadh còmhla, a tha riatanach airson ar misean cruinneil a choileanadh.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Ciad Ainm', required: true },
      { id: 'last_name', label: 'Sloinneadh', required: true },
      { id: 'email', label: 'Seòladh Puist-d', type: 'email', required: true },
      { id: 'phone', label: 'Àireamh Fòn', required: true },
      { id: 'full_address', label: 'Seòladh Slàn (a\' gabhail a-steach an dùthcha)', required: true },
      { id: 'desired_salary', label: 'Tuarastal a Thathar ag Iarraidh', required: true },
      { id: 'start_date', label: 'Ceann-latha Tòiseachaidh', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Cruinneachadh-obrach', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'A bheil thu stèidhichte ann an gin de na dùthchannan seo an-dràsta:',
        name: 'country',
        options: [
          { label: 'Canada', value: 'CA' },
          { label: 'Na Stàitean Aonaichte', value: 'US' },
          { label: 'Chan eil gin de na tha gu h-àrd', value: 'Dùthchannan Eile' },
        ],
      },
    ],
    submit: 'Cuir a-steach',
    requirements: 'Riatanasan',
    responsibilities: 'Dleastanasan',
    uploadCV: 'Luchdaich Suas CV',
    viewMore: 'Seall Barrachd',
    linkInput: {
      smartExtract: 'Togail tapaidh:',
      apply: 'Cuir an gnìomh',
      pasteHelper: 'Cuir a-steach URL slàn airson togail ainm-cleachdaidh gu fèin-obrachail',
      placeholderDefault: '---',
    },
  },

  // Dutch
  nl: {
    header: {
      badge: 'VACATURES',
      title: {
        part1: 'Sluit u bij ons aan',
        highlighted: 'Creëer, Verbind, Innoveer en Ga verder.',
      },
      description:
        'Ontdek boeiende mogelijkheden om deel uit te maken van ons innovatieve team en mee te werken aan de toekomst van AI-gestuurde productontdekking.',
    },
    positions: {
      sectionBadge: 'Openstaande Functies',
      sectionTitle: {
        part1: 'Vind uw',
        highlighted: 'Ideale Functie',
      },
      sectionDescription:
        'Ontdek boeiende mogelijkheden om deel uit te maken van ons innovatieve team en mee te werken aan de toekomst van AI-gestuurde productontdekking.',
    },
    noPositions: {
      title: {
        part1: 'Geen Openstaande Functies',
        highlighted: 'Op dit moment',
      },
      description:
        'Wij zijn altijd op zoek naar getalenteerde mensen. Kom binnenkort terug of neem rechtstreeks contact met ons op om toekomstige mogelijkheden te bespreken.',
      contactButton: 'Neem contact op',
    },
    careers: 'Vacatures',
    carrerFounderMessage: `Ik erken dat het soms moeilijk is om een boek op zijn omslag te beoordelen, en wij zetten ons in om elke sollicitatie zorgvuldig en eerlijk te beoordelen. Als er een mogelijke match is, nemen wij zo snel mogelijk contact met u op. Als u sterk overtuigd bent van uw aansluiting bij ons team, neem dan rechtstreeks contact met ons op via LinkedIn. Wij waarderen de tijd die u hebt genomen om te solliciteren.
    
    -Oli, Oprichter van Inception`,
    careerIntroduction:
      "Onze missie bij Inception is om consumenten wereldwijd te helpen de beste producten in hun regio te vinden met behulp van kunstmatige intelligentie. Met dat doel voor ogen doen wij ook ons best om lokale kleine ondernemers te helpen hun online aanwezigheid te verbeteren.\n\nSluit u bij ons aan om echte problemen op te lossen met AI door lokale bedrijven te ondersteunen en aan uitmuntende technologieën te werken. Samen kunnen wij een betekenisvolle impact hebben op zowel gemeenschappen als consumenten.",
    careerMeta: {
      title: 'Vacatures - Sluit u bij ons aan bij inceptionai.ca',
      description:
        'Sluit u bij ons aan om echte problemen op te lossen met AI door lokale economieën te transformeren en uitstekende producten te leveren. Samen kunnen wij een betekenisvolle impact hebben op zowel gemeenschappen als consumenten.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Onze waarden',
      details: [
        {
          title: 'Klantgericht',
          description:
            'Wij geven prioriteit aan het leveren van ongeëvenaarde ervaringen aan onze klanten, in lijn met onze missie om hen te verbinden met de beste producten.',
        },
        {
          title: 'Innovatie',
          description:
            'Elke dag benutten wij onze AI- en datacapaciteiten om te innoveren en de manieren te verbeteren waarop wij consumenten verbinden met hoogwaardige producten.',
        },
        {
          title: 'Cultuur',
          description:
            'Onze cultuur van leren, samenwerking, transparantie, experimenteren en passie drijft ons om voortdurend te verbeteren en onze missie te vervullen.',
        },
        {
          title: 'Doelgericht',
          description:
            'Toegewijd en doelgericht streven wij naar uitmuntendheid bij het vervullen van onze missie om consumenten te verbinden met superieure producten.',
        },
        {
          title: 'Gemeenschap',
          description:
            'Wij bouwen een ondersteunende gemeenschap die samen floreert, essentieel voor het vervullen van onze wereldwijde missie.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Voornaam', required: true },
      { id: 'last_name', label: 'Achternaam', required: true },
      { id: 'email', label: 'E-mailadres', type: 'email', required: true },
      { id: 'phone', label: 'Telefoonnummer', required: true },
      { id: 'full_address', label: 'Volledig adres (inclusief land)', required: true },
      { id: 'desired_salary', label: 'Gewenst salaris', required: true },
      { id: 'start_date', label: 'Mogelijke startdatum', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portfolio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Bent u momenteel gevestigd in een van deze landen:',
        name: 'country',
        options: [
          { label: 'Canada', value: 'CA' },
          { label: 'Verenigde Staten', value: 'US' },
          { label: 'Geen van bovenstaande', value: 'Andere landen' },
        ],
      },
    ],
    submit: 'Indienen',
    requirements: 'Vereisten',
    responsibilities: 'Verantwoordelijkheden',
    uploadCV: 'CV uploaden',
    viewMore: 'Meer tonen',
    linkInput: {
      smartExtract: 'Slimme extractie:',
      apply: 'Toepassen',
      pasteHelper: 'Plak de volledige URL voor automatische gebruikersnaamextractie',
      placeholderDefault: '---',
    },
  },

  // Bengali
  bn: {
    header: {
      badge: 'ক্যারিয়ার',
      title: {
        part1: 'আমাদের সাথে যোগ দিন',
        highlighted: 'সৃষ্টি করুন, সংযুক্ত হোন, উদ্ভাবন করুন এবং সীমা ছাড়িয়ে যান।',
      },
      description:
        'AI-চালিত পণ্য আবিষ্কারের ভবিষ্যৎ গড়ে তুলতে আমাদের উদ্ভাবনী দলে যোগ দেওয়ার উত্তেজনাপূর্ণ সুযোগগুলি অন্বেষণ করুন।',
    },
    positions: {
      sectionBadge: 'খোলা পদসমূহ',
      sectionTitle: {
        part1: 'আপনার খুঁজুন',
        highlighted: 'আদর্শ ভূমিকা',
      },
      sectionDescription:
        'AI-চালিত পণ্য আবিষ্কারের ভবিষ্যৎ গড়ে তুলতে আমাদের উদ্ভাবনী দলে যোগ দেওয়ার উত্তেজনাপূর্ণ সুযোগগুলি অন্বেষণ করুন।',
    },
    noPositions: {
      title: {
        part1: 'কোনো খোলা পদ নেই',
        highlighted: 'এই মুহূর্তে',
      },
      description:
        'আমরা সর্বদা প্রতিভাবান ব্যক্তিদের খুঁজি। শীঘ্রই আবার দেখুন অথবা ভবিষ্যতের সুযোগ নিয়ে আলোচনা করতে সরাসরি আমাদের সাথে যোগাযোগ করুন।',
      contactButton: 'যোগাযোগ করুন',
    },
    careers: 'ক্যারিয়ার',
    carrerFounderMessage: `আমি স্বীকার করি যে কখনও কখনও বাইরের দিক দেখে কোনো কিছু সম্পর্কে সিদ্ধান্ত নেওয়া কঠিন, এবং আমরা প্রতিটি আবেদন পুঙ্খানুপুঙ্খভাবে ও ন্যায্যভাবে পর্যালোচনা করতে প্রতিশ্রুতিবদ্ধ। যদি সম্ভাব্য মিল থাকে, আমরা দ্রুত আপনার সাথে যোগাযোগ করব। আপনি যদি আমাদের দলের সাথে আপনার সামঞ্জস্য সম্পর্কে দৃঢ়ভাবে অনুভব করেন, তাহলে অনুগ্রহ করে LinkedIn-এ সরাসরি আমাদের সাথে যোগাযোগ করুন। আবেদন করতে আপনি যে সময় দিয়েছেন তার জন্য আমরা কৃতজ্ঞ।
    
    -Oli, Inception প্রতিষ্ঠাতা`,
    careerIntroduction:
      "Inception-তে আমাদের লক্ষ্য হলো কৃত্রিম বুদ্ধিমত্তার সাহায্যে সারা বিশ্বের ভোক্তাদের তাদের অঞ্চলের সেরা পণ্য খুঁজে পেতে সাহায্য করা। এই লক্ষ্য মাথায় রেখে, আমরা স্থানীয় ছোট ব্যবসার মালিকদের তাদের অনলাইন উপস্থিতি উন্নত করতে সাহায্য করার জন্যও সর্বোচ্চ চেষ্টা করি।\n\nস্থানীয় ব্যবসাগুলিকে সাহায্য করে এবং অসাধারণ প্রযুক্তিতে কাজ করে AI দিয়ে বাস্তব সমস্যা সমাধানে আমাদের সাথে যোগ দিন। একসাথে, আমরা সম্প্রদায় এবং ভোক্তা উভয়ের উপরই একটি অর্থবহ প্রভাব ফেলতে পারি।",
    careerMeta: {
      title: 'ক্যারিয়ার - inceptionai.ca-তে আমাদের সাথে যোগ দিন',
      description:
        'স্থানীয় অর্থনীতিকে রূপান্তরিত করে এবং অসাধারণ পণ্য সরবরাহ করে AI দিয়ে বাস্তব সমস্যা সমাধানে আমাদের সাথে যোগ দিন। একসাথে, আমরা সম্প্রদায় এবং ভোক্তা উভয়ের উপরই একটি অর্থবহ প্রভাব ফেলতে পারি।',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'আমাদের মূল্যবোধ',
      details: [
        {
          title: 'গ্রাহক কেন্দ্রিক',
          description:
            'আমরা আমাদের গ্রাহকদের অতুলনীয় অভিজ্ঞতা প্রদানকে অগ্রাধিকার দিই, তাদের সেরা পণ্যের সাথে সংযুক্ত করার আমাদের লক্ষ্যের সাথে সামঞ্জস্য রেখে।',
        },
        {
          title: 'উদ্ভাবন',
          description:
            'প্রতিদিন, আমরা ভোক্তাদের উচ্চমানের পণ্যের সাথে সংযুক্ত করার উপায়গুলি উদ্ভাবন ও উন্নত করতে আমাদের AI এবং বিশাল ডেটা সক্ষমতা ব্যবহার করি।',
        },
        {
          title: 'সংস্কৃতি',
          description:
            'শিক্ষা, সহযোগিতা, স্বচ্ছতা, পরীক্ষা-নিরীক্ষা এবং আবেগের আমাদের সংস্কৃতি আমাদের ক্রমাগত উন্নতি করতে এবং আমাদের লক্ষ্য পূরণ করতে অনুপ্রাণিত করে।',
        },
        {
          title: 'লক্ষ্যনিষ্ঠ',
          description:
            'নিবেদিত এবং লক্ষ্যমুখী, আমরা ভোক্তাদের উৎকৃষ্ট পণ্যের সাথে সংযুক্ত করার আমাদের লক্ষ্যে শ্রেষ্ঠত্বের জন্য প্রচেষ্টা করি।',
        },
        {
          title: 'সম্প্রদায়',
          description:
            'আমরা একটি সহায়ক সম্প্রদায় গড়ে তুলি এবং লালন করি যা একসাথে সমৃদ্ধ হয়, যা আমাদের বৈশ্বিক লক্ষ্য পূরণের জন্য অত্যন্ত গুরুত্বপূর্ণ।',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'প্রথম নাম', required: true },
      { id: 'last_name', label: 'পদবি', required: true },
      { id: 'email', label: 'ইমেইল ঠিকানা', type: 'email', required: true },
      { id: 'phone', label: 'ফোন নম্বর', required: true },
      { id: 'full_address', label: 'সম্পূর্ণ ঠিকানা (দেশসহ)', required: true },
      { id: 'desired_salary', label: 'প্রত্যাশিত বেতন', required: true },
      { id: 'start_date', label: 'সম্ভাব্য শুরুর তারিখ', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'পোর্টফোলিও', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'আপনি কি বর্তমানে এই দেশগুলির কোনোটিতে আছেন:',
        name: 'country',
        options: [
          { label: 'কানাডা', value: 'CA' },
          { label: 'যুক্তরাষ্ট্র', value: 'US' },
          { label: 'উপরের কোনোটিই নয়', value: 'অন্যান্য দেশ' },
        ],
      },
    ],
    submit: 'জমা দিন',
    requirements: 'প্রয়োজনীয়তা',
    responsibilities: 'দায়িত্বসমূহ',
    uploadCV: 'CV আপলোড করুন',
    viewMore: 'আরও দেখুন',
    linkInput: {
      smartExtract: 'স্মার্ট এক্সট্র্যাক্ট:',
      apply: 'প্রয়োগ করুন',
      pasteHelper: 'স্বয়ংক্রিয় ব্যবহারকারীর নাম নিষ্কাশনের জন্য সম্পূর্ণ URL পেস্ট করুন',
      placeholderDefault: '---',
    },
  },

  // Welsh
  cy: {
    header: {
      badge: 'GYRFAOEDD',
      title: {
        part1: 'Ymunwch â Ni',
        highlighted: 'Creu, Cysylltu, Arloesi, a Mynd Tu Hwnt.',
      },
      description:
        'Archwiliwch gyfleoedd cyffrous i ymuno â\'n tîm arloesol a helpu i lunio dyfodol darganfod cynhyrchion a yrrir gan AI.',
    },
    positions: {
      sectionBadge: 'Swyddi Agored',
      sectionTitle: {
        part1: 'Dewch o Hyd i\'ch',
        highlighted: 'Rôl Berffaith',
      },
      sectionDescription:
        'Archwiliwch gyfleoedd cyffrous i ymuno â\'n tîm arloesol a helpu i lunio dyfodol darganfod cynhyrchion a yrrir gan AI.',
    },
    noPositions: {
      title: {
        part1: 'Dim Swyddi Agored',
        highlighted: 'Ar Hyn o Bryd',
      },
      description:
        'Rydym bob amser yn chwilio am unigolion talentog. Dewch yn ôl yn fuan neu cysylltwch â ni yn uniongyrchol i drafod cyfleoedd yn y dyfodol.',
      contactButton: 'Cysylltwch â Ni',
    },
    careers: 'Gyrfaoedd',
    carrerFounderMessage: `Rwy\'n cydnabod ei bod hi weithiau\'n anodd barnu llyfr wrth ei glawr, ac rydym wedi ymrwymo i adolygu pob cais yn drylwyr ac yn deg. Os oes cydweddiad posibl, byddwn yn cysylltu â chi ar unwaith. Os ydych chi\'n teimlo\'n gryf am eich cydweddiad â\'n tîm, cysylltwch â ni yn uniongyrchol ar LinkedIn. Rydym yn gwerthfawrogi\'r amser a gymeroch chi i wneud cais.
    
    -Oli, Sylfaenydd Inception`,
    careerIntroduction:
      "Ein cenhadaeth yn Inception yw helpu defnyddwyr ledled y byd i ddod o hyd i'r cynhyrchion gorau yn eu rhanbarth gyda deallusrwydd artiffisial. Gyda'r nod hwnnw mewn golwg, rydym hefyd yn gwneud ein gorau i helpu perchnogion busnesau bach lleol i wella eu presenoldeb ar-lein.\n\nYmunwch â ni i ddatrys problemau byd go iawn gydag AI drwy helpu busnesau lleol a gweithio ar dechnolegau rhagorol. Gyda'n gilydd, gallwn gael effaith ystyrlon ar gymunedau a defnyddwyr fel ei gilydd.",
    careerMeta: {
      title: 'Gyrfa - Ymunwch â Ni yn inceptionai.ca',
      description:
        'Ymunwch â ni i ddatrys problemau byd go iawn gydag AI drwy drawsnewid economïau lleol a darparu cynhyrchion rhagorol. Gyda\'n gilydd, gallwn gael effaith ystyrlon ar gymunedau a defnyddwyr fel ei gilydd.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Ein Gwerthoedd',
      details: [
        {
          title: 'Obsesiwn â\'r Cwsmer',
          description:
            'Rydym yn blaenoriaethu darparu profiadau digymar i\'n cwsmeriaid, yn unol â\'n cenhadaeth i\'w cysylltu â\'r cynhyrchion gorau.',
        },
        {
          title: 'Arloesedd',
          description:
            'Bob dydd, rydym yn defnyddio ein galluoedd AI a data enfawr i arloesi a gwella\'r ffyrdd rydym yn cysylltu defnyddwyr â chynhyrchion o\'r ansawdd uchaf.',
        },
        {
          title: 'Diwylliant',
          description:
            'Mae ein diwylliant o ddysgu, cydweithio, tryloywder, arbrofi ac angerdd yn ein gyrru i wella\'n barhaus a chyflawni ein cenhadaeth.',
        },
        {
          title: 'Wedi\'n Gyrru gan Nodau',
          description:
            'Yn ymroddedig ac yn canolbwyntio ar nodau, rydym yn ymdrechu am ragoriaeth wrth gyflawni ein cenhadaeth o gysylltu defnyddwyr â chynhyrchion rhagorol.',
        },
        {
          title: 'Cymuned',
          description:
            'Rydym yn adeiladu ac yn meithrin cymuned gefnogol sy\'n ffynnu gyda\'i gilydd, sy\'n hanfodol ar gyfer cyflawni ein cenhadaeth fyd-eang.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Enw Cyntaf', required: true },
      { id: 'last_name', label: 'Cyfenw', required: true },
      { id: 'email', label: 'Cyfeiriad E-bost', type: 'email', required: true },
      { id: 'phone', label: 'Rhif Ffôn', required: true },
      { id: 'full_address', label: 'Cyfeiriad Llawn (gan gynnwys gwlad)', required: true },
      { id: 'desired_salary', label: 'Cyflog Dymunol', required: true },
      { id: 'start_date', label: 'Dyddiad Dechrau Posibl', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portffolio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'A ydych chi wedi eich lleoli ar hyn o bryd yn un o\'r gwledydd hyn:',
        name: 'country',
        options: [
          { label: 'Canada', value: 'CA' },
          { label: 'Yr Unol Daleithiau', value: 'US' },
          { label: 'Dim un o\'r uchod', value: 'Gwledydd Eraill' },
        ],
      },
    ],
    submit: 'Cyflwyno',
    requirements: 'Gofynion',
    responsibilities: 'Cyfrifoldebau',
    uploadCV: 'Uwchlwytho CV',
    viewMore: 'Dangos Mwy',
    linkInput: {
      smartExtract: 'Echdynnu deallus:',
      apply: 'Cymhwyso',
      pasteHelper: 'Gludwch yr URL llawn ar gyfer echdynnu enw defnyddiwr yn awtomatig',
      placeholderDefault: '---',
    },
  },

  // Marathi
  mr: {
    header: {
      badge: 'करिअर',
      title: {
        part1: 'आमच्यासोबत या',
        highlighted: 'निर्माण करा, जोडा, नवनिर्मिती करा आणि पुढे जा.',
      },
      description:
        'AI-आधारित उत्पादन शोधाचे भविष्य घडवण्यासाठी आमच्या नाविन्यपूर्ण संघात सामील होण्याच्या रोमांचक संधी शोधा.',
    },
    positions: {
      sectionBadge: 'रिक्त पदे',
      sectionTitle: {
        part1: 'तुमची शोधा',
        highlighted: 'आदर्श भूमिका',
      },
      sectionDescription:
        'AI-आधारित उत्पादन शोधाचे भविष्य घडवण्यासाठी आमच्या नाविन्यपूर्ण संघात सामील होण्याच्या रोमांचक संधी शोधा.',
    },
    noPositions: {
      title: {
        part1: 'सध्या कोणतीही रिक्त पदे नाहीत',
        highlighted: 'या क्षणी',
      },
      description:
        'आम्ही नेहमीच प्रतिभावान व्यक्तींच्या शोधात असतो. लवकरच पुन्हा भेट द्या किंवा भविष्यातील संधींबद्दल चर्चा करण्यासाठी आमच्याशी थेट संपर्क साधा.',
      contactButton: 'संपर्क साधा',
    },
    careers: 'करिअर',
    carrerFounderMessage: `मला माहीत आहे की कधीकधी पुस्तकाचे मुखपृष्ठ पाहून त्याबद्दल मत बनवणे कठीण असते, आणि आम्ही प्रत्येक अर्जाचे सखोल आणि निष्पक्ष पुनरावलोकन करण्यासाठी वचनबद्ध आहोत. जर संभाव्य जुळणी असेल, तर आम्ही तुमच्याशी त्वरित संपर्क साधू. तुम्हाला आमच्या संघाशी तुमची सुसंगतता याबद्दल दृढ विश्वास असल्यास, कृपया LinkedIn वर आमच्याशी थेट संपर्क साधा. तुम्ही अर्ज करण्यासाठी दिलेल्या वेळेची आम्ही कदर करतो.
    
    -Oli, Inception संस्थापक`,
    careerIntroduction:
      "Inception मधील आमचे ध्येय कृत्रिम बुद्धिमत्तेच्या साहाय्याने जगभरातील ग्राहकांना त्यांच्या प्रदेशातील सर्वोत्तम उत्पादने शोधण्यात मदत करणे हे आहे. हे उद्दिष्ट लक्षात ठेवून, आम्ही स्थानिक लघु उद्योग मालकांना त्यांची ऑनलाइन उपस्थिती वाढवण्यासाठी मदत करण्याचा सर्वतोपरी प्रयत्न करतो.\n\nस्थानिक व्यवसायांना मदत करून आणि उत्कृष्ट तंत्रज्ञानावर काम करून AI सह वास्तविक समस्या सोडवण्यासाठी आमच्यासोबत या. एकत्रितपणे, आपण समुदाय आणि ग्राहक दोघांवरही सकारात्मक प्रभाव टाकू शकतो.",
    careerMeta: {
      title: 'करिअर - inceptionai.ca मध्ये आमच्यासोबत या',
      description:
        'स्थानिक अर्थव्यवस्था बदलून आणि उत्कृष्ट उत्पादने देऊन AI सह वास्तविक समस्या सोडवण्यासाठी आमच्यासोबत या. एकत्रितपणे, आपण समुदाय आणि ग्राहक दोघांवरही सकारात्मक प्रभाव टाकू शकतो.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'आमची मूल्ये',
      details: [
        {
          title: 'ग्राहक केंद्रित',
          description:
            'आमच्या ग्राहकांना अतुलनीय अनुभव देण्यास आम्ही प्राधान्य देतो, त्यांना सर्वोत्तम उत्पादनांशी जोडण्याच्या आमच्या ध्येयाशी सुसंगत.',
        },
        {
          title: 'नवनिर्मिती',
          description:
            'दररोज, आम्ही ग्राहकांना उच्च दर्जाच्या उत्पादनांशी जोडण्याचे मार्ग सुधारण्यासाठी आणि नवनिर्मिती करण्यासाठी आमच्या AI आणि मोठ्या डेटा क्षमतांचा वापर करतो.',
        },
        {
          title: 'संस्कृती',
          description:
            'शिकणे, सहकार्य, पारदर्शकता, प्रयोग आणि उत्कटता यांची आमची संस्कृती आम्हाला सतत सुधारणा करण्यासाठी आणि आमचे ध्येय पूर्ण करण्यासाठी प्रेरित करते.',
        },
        {
          title: 'ध्येयनिष्ठ',
          description:
            'समर्पित आणि ध्येयनिष्ठ, आम्ही ग्राहकांना उत्कृष्ट उत्पादनांशी जोडण्याच्या आमच्या ध्येयात उत्कृष्टतेसाठी प्रयत्न करतो.',
        },
        {
          title: 'समुदाय',
          description:
            'आम्ही एक सहाय्यक समुदाय तयार करतो आणि जोपासतो जो एकत्र भरभराट करतो, जे आमचे जागतिक ध्येय पूर्ण करण्यासाठी अत्यंत महत्त्वाचे आहे.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'पहिले नाव', required: true },
      { id: 'last_name', label: 'आडनाव', required: true },
      { id: 'email', label: 'ईमेल पत्ता', type: 'email', required: true },
      { id: 'phone', label: 'फोन नंबर', required: true },
      { id: 'full_address', label: 'पूर्ण पत्ता (देशासह)', required: true },
      { id: 'desired_salary', label: 'अपेक्षित वेतन', required: true },
      { id: 'start_date', label: 'संभाव्य सुरुवातीची तारीख', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'पोर्टफोलिओ', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'तुम्ही सध्या यापैकी कोणत्या देशात राहता:',
        name: 'country',
        options: [
          { label: 'कॅनडा', value: 'CA' },
          { label: 'अमेरिका', value: 'US' },
          { label: 'वरीलपैकी कोणतेही नाही', value: 'इतर देश' },
        ],
      },
    ],
    submit: 'सबमिट करा',
    requirements: 'आवश्यकता',
    responsibilities: 'जबाबदाऱ्या',
    uploadCV: 'CV अपलोड करा',
    viewMore: 'अधिक दाखवा',
    linkInput: {
      smartExtract: 'स्मार्ट एक्स्ट्रॅक्ट:',
      apply: 'लागू करा',
      pasteHelper: 'स्वयंचलित वापरकर्तानाव एक्स्ट्रॅक्शनसाठी पूर्ण URL पेस्ट करा',
      placeholderDefault: '---',
    },
  },

  // Basque
  eu: {
    header: {
      badge: 'KARRERAK',
      title: {
        part1: 'Bat egin gurekin',
        highlighted: 'Sortu, Konektatu, Berritu eta Joan Haratago.',
      },
      description:
        'Esploratu aukera zirraragarriak gure talde berritzailean sartzeko eta AAren bidezko produktuen aurkikuntzan etorkizuna moldatzen laguntzeko.',
    },
    positions: {
      sectionBadge: 'Lanpostu irekiak',
      sectionTitle: {
        part1: 'Aurkitu zure',
        highlighted: 'Eginkizun ezin hobea',
      },
      sectionDescription:
        'Esploratu aukera zirraragarriak gure talde berritzailean sartzeko eta AAren bidezko produktuen aurkikuntzan etorkizuna moldatzen laguntzeko.',
    },
    noPositions: {
      title: {
        part1: 'Ez dago lanpostu irekirik',
        highlighted: 'Une honetan',
      },
      description:
        'Beti bilatzen ari gara pertsona talentudunak. Itzuli laster edo jarri gurekin zuzenean harremanetan etorkizuneko aukerez hitz egiteko.',
      contactButton: 'Jarri harremanetan',
    },
    careers: 'Karrerak',
    carrerFounderMessage: `Aitortzen dut batzuetan zaila dela liburu bat azaletik epaitzea, eta konpromisoa dugu eskaera bakoitza sakonki eta zuzen aztertzeko. Bat-etortze potentzialik badago, berehala jarriko gara zurekin harremanetan. Gure taldearekin lerrokatua sentitzen bazara, mesedez jarri gurekin zuzenean harremanetan LinkedIn-en. Eskaintza aurkezteko hartutako denbora eskertzen dugu.
    
    -Oli, Inception-ren sortzailea`,
    careerIntroduction:
      "Inception-n gure helburua da munduko kontsumitzaileei beren eskualdeko produktu onenak adimen artifizialarekin aurkitzen laguntzea. Helburu hori kontuan hartuta, gure ahalegina egiten dugu tokiko enpresa txikien jabeei beren lineako presentzia hobetzen laguntzeko.\n\nBat egin gurekin mundu errealeko arazoak AArekin konpontzeko, tokiko enpresak lagunduz eta teknologia bikainetan lan eginez. Elkarrekin, eragin esanguratsua izan dezakegu bai komunitateetan bai kontsumitzaileengan.",
    careerMeta: {
      title: 'Karrera - Bat egin gurekin inceptionai.ca-n',
      description:
        'Bat egin gurekin mundu errealeko arazoak AArekin konpontzeko, tokiko ekonomiak eraldatuz eta produktu bikainak eskainiz. Elkarrekin, eragin esanguratsua izan dezakegu bai komunitateetan bai kontsumitzaileengan.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Gure balioak',
      details: [
        {
          title: 'Bezeroaren obsesioa',
          description:
            'Lehentasuna ematen diogu gure bezeroei esperientzia paregabeak emateari, gure helburuarekin lerrokatuz produktu onenekin konektatzeko.',
        },
        {
          title: 'Berrikuntza',
          description:
            'Egunero gure AA eta datu-gaitasunak erabiltzen ditugu berritzeko eta kontsumitzaileak kalitate handiko produktuekin konektatzeko moduak hobetzeko.',
        },
        {
          title: 'Kultura',
          description:
            'Ikaskuntza, lankidetza, gardentasuna, esperimentazioa eta pasioaren gure kulturak etengabe hobetzera eta gure helburua betetzera bultzatzen gaitu.',
        },
        {
          title: 'Helburuetara bideratua',
          description:
            'Dedikatua eta helburuetara bideratua, bikaintasunaren bila gabiltza kontsumitzaileak produktu gorenarekin konektatzen.',
        },
        {
          title: 'Komunitatea',
          description:
            'Elkarrekin hazten den laguntza-komunitatea eraikitzen eta zaintzen dugu, funtsezkoa gure helburu globalerako.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Izena', required: true },
      { id: 'last_name', label: 'Abizena', required: true },
      { id: 'email', label: 'Helbide elektronikoa', type: 'email', required: true },
      { id: 'phone', label: 'Telefono-zenbakia', required: true },
      { id: 'full_address', label: 'Helbide osoa (herrialdea barne)', required: true },
      { id: 'desired_salary', label: 'Nahi den soldata', required: true },
      { id: 'start_date', label: 'Hasiera-data posiblea', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portfolioa', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Gaur egun herrialde hauetakoren batean bizi zara:',
        name: 'country',
        options: [
          { label: 'Kanada', value: 'CA' },
          { label: 'Estatu Batuak', value: 'US' },
          { label: 'Aurreko bat ere ez', value: 'Beste herrialdeak' },
        ],
      },
    ],
    submit: 'Bidali',
    requirements: 'Eskakizunak',
    responsibilities: 'Erantzukizunak',
    uploadCV: 'CV-a igo',
    viewMore: 'Gehiago erakutsi',
    linkInput: {
      smartExtract: 'Erauzketa adimentsua:',
      apply: 'Aplikatu',
      pasteHelper: 'Itsatsi URL osoa erabiltzaile-izenaren erauzketa automatikorako',
      placeholderDefault: '---',
    },
  },

  // Hindi
  hi: {
    header: {
      badge: 'करियर',
      title: {
        part1: 'हमसे जुड़ें',
        highlighted: 'रचना करें, जुड़ें, नवाचार करें और आगे बढ़ें।',
      },
      description:
        'AI-संचालित उत्पाद खोज के भविष्य को आकार देने में हमारी नवाचारी टीम से जुड़ने के रोमांचक अवसर खोजें।',
    },
    positions: {
      sectionBadge: 'खुली पदें',
      sectionTitle: {
        part1: 'अपनी खोजें',
        highlighted: 'आदर्श भूमिका',
      },
      sectionDescription:
        'AI-संचालित उत्पाद खोज के भविष्य को आकार देने में हमारी नवाचारी टीम से जुड़ने के रोमांचक अवसर खोजें।',
    },
    noPositions: {
      title: {
        part1: 'इस समय कोई खुली पद नहीं',
        highlighted: 'अभी',
      },
      description:
        'हम हमेशा प्रतिभाशाली व्यक्तियों की तलाश में रहते हैं। कृपया जल्द ही पुनः देखें या भविष्य के अवसरों पर चर्चा करने के लिए हमसे सीधे संपर्क करें।',
      contactButton: 'संपर्क करें',
    },
    careers: 'करियर',
    carrerFounderMessage: `मैं समझता हूँ कि कभी-कभी किसी किताब को उसके कवर से आंकना कठिन होता है, और हम हर आवेदन की पूरी तरह और निष्पक्ष रूप से समीक्षा करने के लिए प्रतिबद्ध हैं। यदि कोई संभावित उपयुक्तता है, तो हम आपसे शीघ्र संपर्क करेंगे। यदि आप हमारी टीम के साथ अपनी अनुकूलता को लेकर दृढ़ विश्वास रखते हैं, तो कृपया LinkedIn पर हमसे सीधे संपर्क करें। आपने आवेदन करने में जो समय दिया, उसकी हम सराहना करते हैं।
    
    -Oli, Inception संस्थापक`,
    careerIntroduction:
      "Inception में हमारा लक्ष्य कृत्रिम बुद्धिमत्ता की सहायता से दुनिया भर के उपभोक्ताओं को उनके क्षेत्र में सर्वोत्तम उत्पाद खोजने में मदद करना है। इस उद्देश्य को ध्यान में रखते हुए, हम स्थानीय लघु व्यवसाय मालिकों को उनकी ऑनलाइन उपस्थिति बढ़ाने में भी सहायता करने का हर संभव प्रयास कर रहे हैं।\n\nस्थानीय व्यवसायों की सहायता करके और उत्कृष्ट प्रौद्योगिकियों पर काम करके AI के माध्यम से वास्तविक समस्याओं को हल करने में हमसे जुड़ें। साथ मिलकर, हम समुदायों और उपभोक्ताओं दोनों पर सार्थक प्रभाव डाल सकते हैं।",
    careerMeta: {
      title: 'करियर - inceptionai.ca में हमसे जुड़ें',
      description:
        'स्थानीय अर्थव्यवस्थाओं को बदलकर और उत्कृष्ट उत्पाद प्रदान करके AI के माध्यम से वास्तविक समस्याओं को हल करने में हमसे जुड़ें। साथ मिलकर, हम समुदायों और उपभोक्ताओं दोनों पर सार्थक प्रभाव डाल सकते हैं।',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'हमारे मूल्य',
      details: [
        {
          title: 'ग्राहक केंद्रित',
          description:
            'हम अपने ग्राहकों को अद्वितीय अनुभव प्रदान करने को प्राथमिकता देते हैं, जो उन्हें सर्वोत्तम उत्पादों से जोड़ने के हमारे लक्ष्य के अनुरूप है।',
        },
        {
          title: 'नवाचार',
          description:
            'हर दिन, हम उपभोक्ताओं को उच्च गुणवत्ता वाले उत्पादों से जोड़ने के तरीकों में नवाचार और सुधार करने के लिए अपनी AI और विशाल डेटा क्षमताओं का उपयोग करते हैं।',
        },
        {
          title: 'संस्कृति',
          description:
            'सीखने, सहयोग, पारदर्शिता, प्रयोग और जुनून की हमारी संस्कृति हमें निरंतर सुधार करने और अपने लक्ष्य को पूरा करने के लिए प्रेरित करती है।',
        },
        {
          title: 'लक्ष्य केंद्रित',
          description:
            'समर्पित और लक्ष्य-उन्मुख, हम उपभोक्ताओं को श्रेष्ठ उत्पादों से जोड़ने के अपने लक्ष्य में उत्कृष्टता के लिए प्रयास करते हैं।',
        },
        {
          title: 'समुदाय',
          description:
            'हम एक सहायक समुदाय का निर्माण और पोषण करते हैं जो एक साथ फलता-फूलता है, जो हमारे वैश्विक लक्ष्य को पूरा करने के लिए अत्यंत महत्वपूर्ण है।',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'पहला नाम', required: true },
      { id: 'last_name', label: 'उपनाम', required: true },
      { id: 'email', label: 'ईमेल पता', type: 'email', required: true },
      { id: 'phone', label: 'फ़ोन नंबर', required: true },
      { id: 'full_address', label: 'पूरा पता (देश सहित)', required: true },
      { id: 'desired_salary', label: 'अपेक्षित वेतन', required: true },
      { id: 'start_date', label: 'संभावित आरंभ तिथि', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'पोर्टफोलियो', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'क्या आप वर्तमान में इनमें से किसी देश में रहते हैं:',
        name: 'country',
        options: [
          { label: 'कनाडा', value: 'CA' },
          { label: 'संयुक्त राज्य अमेरिका', value: 'US' },
          { label: 'उपरोक्त में से कोई नहीं', value: 'Other Countries' },
        ],
      },
    ],
    submit: 'सबमिट करें',
    requirements: 'आवश्यकताएँ',
    responsibilities: 'जिम्मेदारियाँ',
    uploadCV: 'CV अपलोड करें',
    viewMore: 'और देखें',
    linkInput: {
      smartExtract: 'स्मार्ट एक्सट्रैक्ट:',
      apply: 'लागू करें',
      pasteHelper: 'स्वचालित उपयोगकर्ता नाम निकालने के लिए पूरा URL पेस्ट करें',
      placeholderDefault: '---',
    },
  },

  // Galician
  gl: {
    header: {
      badge: 'CARREIRAS',
      title: {
        part1: 'Únase a nós',
        highlighted: 'Crear, Conectar, Innovar e Ir Máis Alá.',
      },
      description:
        'Explore oportunidades emocionantes para unirse ao noso equipo innovador e axudar a dar forma ao futuro do descubrimento de produtos impulsado pola IA.',
    },
    positions: {
      sectionBadge: 'Postos abertos',
      sectionTitle: {
        part1: 'Atope o seu',
        highlighted: 'Rol perfecto',
      },
      sectionDescription:
        'Explore oportunidades emocionantes para unirse ao noso equipo innovador e axudar a dar forma ao futuro do descubrimento de produtos impulsado pola IA.',
    },
    noPositions: {
      title: {
        part1: 'Non hai postos abertos',
        highlighted: 'Neste momento',
      },
      description:
        'Sempre estamos á procura de persoas con talento. Volva pronto ou contacte connosco directamente para comentar oportunidades futuras.',
      contactButton: 'Poñerse en contacto',
    },
    careers: 'Carreiras',
    carrerFounderMessage: `Recoñezo que ás veces é difícil xulgar un libro pola súa portada, e estamos comprometidos a revisar cada candidatura de forma completa e xusta. Se hai unha posíbel coincidencia, contactaremos con vostede axiña. Se sente un forte aliñamento co noso equipo, contacte connosco directamente en LinkedIn. Agradecemos o tempo que dedicou a presentar a súa candidatura.
    
    -Oli, Fundador de Inception`,
    careerIntroduction:
      "A nosa misión en Inception é axudar aos consumidores de todo o mundo a atopar os mellores produtos na súa rexión con intelixencia artificial. Con ese obxectivo en mente, tamén facemos o noso mellor para axudar aos propietarios de pequenas empresas locais a mellorar a súa presenza en liña.\n\nÚnase a nós para resolver problemas reais con IA axudando a empresas locais e traballando en tecnoloxías extraordinarias. Xuntos, podemos ter un impacto significativo tanto nas comunidades como nos consumidores.",
    careerMeta: {
      title: 'Carreira - Únase a nós en inceptionai.ca',
      description:
        'Únase a nós para resolver problemas reais con IA transformando economías locais e ofrecendo produtos extraordinarios. Xuntos, podemos ter un impacto significativo tanto nas comunidades como nos consumidores.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Os nosos valores',
      details: [
        {
          title: 'Obsesionados polo cliente',
          description:
            'Priorizamos ofrecer experiencias incomparábeis aos nosos clientes, aliñados coa nosa misión de conectalos cos mellores produtos.',
        },
        {
          title: 'Innovación',
          description:
            'Cada día aproveitamos as nosas capacidades de IA e datos para innovar e mellorar as formas en que conectamos consumidores con produtos de alta calidade.',
        },
        {
          title: 'Cultura',
          description:
            'A nosa cultura de aprendizaxe, colaboración, transparencia, experimentación e paixón impúlsanos a mellorar continuamente e cumprir a nosa misión.',
        },
        {
          title: 'Orientados a obxectivos',
          description:
            'Dedicados e orientados a obxectivos, buscamos a excelencia no cumprimento da nosa misión de conectar consumidores con produtos superiores.',
        },
        {
          title: 'Comunidade',
          description:
            'Construímos e cultivamos unha comunidade de apoio que prospera en conxunto, esencial para cumprir a nosa misión global.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Nome', required: true },
      { id: 'last_name', label: 'Apelidos', required: true },
      { id: 'email', label: 'Enderezo de correo electrónico', type: 'email', required: true },
      { id: 'phone', label: 'Número de teléfono', required: true },
      { id: 'full_address', label: 'Enderezo completo (incluíndo país)', required: true },
      { id: 'desired_salary', label: 'Salario desexado', required: true },
      { id: 'start_date', label: 'Data de inicio potencial', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portafolio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Está actualmente nalgún destes países:',
        name: 'country',
        options: [
          { label: 'Canadá', value: 'CA' },
          { label: 'Estados Unidos', value: 'US' },
          { label: 'Ningún dos anteriores', value: 'Outros Países' },
        ],
      },
    ],
    submit: 'Enviar',
    requirements: 'Requisitos',
    responsibilities: 'Responsabilidades',
    uploadCV: 'Cargar CV',
    viewMore: 'Mostrar máis',
    linkInput: {
      smartExtract: 'Extracción intelixente:',
      apply: 'Aplicar',
      pasteHelper: 'Pegue o URL completo para a extracción automática do nome de usuario',
      placeholderDefault: '---',
    },
  },

  // Romansh
  rm: {
    header: {
      badge: 'CARRIERAS',
      title: {
        part1: 'Accumpagnai nus',
        highlighted: 'Crear, colliar, innovar e surpassar.',
      },
      description:
        'Explorai pussaivladads fascinantas per ans accumpagnar en nossa squadra innovativa e contribuir a furmar l\'avegnir da la scuverta da products cun IA.',
    },
    positions: {
      sectionBadge: 'Posts averts',
      sectionTitle: {
        part1: 'Chattai Voss',
        highlighted: 'Rolla perfecta',
      },
      sectionDescription:
        'Explorai pussaivladads fascinantas per ans accumpagnar en nossa squadra innovativa e contribuir a furmar l\'avegnir da la scuverta da products cun IA.',
    },
    noPositions: {
      title: {
        part1: 'Nagins posts averts',
        highlighted: 'Il mument',
      },
      description:
        'Nus tschertgain adina persunas talentuosas. Turnai en brev u contactai nus directamain per discutar pussaivladads futuras.',
      contactButton: 'Prender contact',
    },
    careers: 'Carrieras',
    carrerFounderMessage: `Jau enconusch ch\'igl è mintgatant grev da giuditgar in cudesch tenor sia cuvrida, e nus ans oblighain da reveder mintga candidatura temprà e gista. Sch'i dat ina concordanza potenziala, contactain nus Vus il pli svelt pussaivel. Sche Vus essas persvas da Vossa concordanza cun nossa squadra, contactai nus directamain sin LinkedIn. Nus engrazian per il temp che Vus avais investì per candidar.
    
    -Oli, fundatur da Inception`,
    careerIntroduction:
      "Nossa missiun tar Inception è d'agitar consuments da tut il mund a chattar ils megliers products en lur regiun cun intelligenza artifiziala. Cun quest intent, faschain nus era noss meglier per agitar possessurs da pitschnas firmas localas a megliereschar lur presence online.\n\nAccumpagnai nus per schliar problems reals cun IA, agitond firmas localas e lavurand vid tecnologias extraordinarias. Ensemen pudain nus avair in effect significativ sin communitads e consuments.",
    careerMeta: {
      title: 'Carriera - Accumpagnai nus tar inceptionai.ca',
      description:
        'Accumpagnai nus per schliar problems reals cun IA, transfurmand economiasvi localas e purschend products extraordinaris. Ensemen pudain nus avair in effect significativ sin communitads e consuments.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Noss valurs',
      details: [
        {
          title: 'Focussads sin il client',
          description:
            'Nus prioriain da purtar experientschas senza para a noss clients, en concordanza cun nossa missiun da als colliar cun ils megliers products.',
        },
        {
          title: 'Innovaziun',
          description:
            'Mintga di utilisain nus nossas capacitads dad IA e datas per innovar e megliereschar las vias co nus colliain consuments cun products d\'auta qualitad.',
        },
        {
          title: 'Cultura',
          description:
            'Nossa cultura d\'emprender, collaboraziun, transparenza, experimentaziun e passiun ans muenta da megliereschar cuntinuadamain e da cumplentar nossa missiun.',
        },
        {
          title: 'Orientads vers finamiras',
          description:
            'Dedicads e orientads vers finamiras, aspirain nus ad excellenza en la realisaziun da nossa missiun da colliar consuments cun products superiurs.',
        },
        {
          title: 'Communitad',
          description:
            'Nus construin e cultivain ina communitad da sustegn che prosperescha ensemen, essenzial per cumplentar nossa missiun globala.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Prenum', required: true },
      { id: 'last_name', label: 'Surnum', required: true },
      { id: 'email', label: 'Adressa dad e-mail', type: 'email', required: true },
      { id: 'phone', label: 'Numer da telefon', required: true },
      { id: 'full_address', label: 'Adressa cumpletta (cun pajais)', required: true },
      { id: 'desired_salary', label: 'Salari giavischà', required: true },
      { id: 'start_date', label: 'Data da cumenzament potenziala', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portfolio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Essas Vus actualmain domiciliads en in da quests pajais:',
        name: 'country',
        options: [
          { label: 'Canada', value: 'CA' },
          { label: 'Stadis Unids', value: 'US' },
          { label: 'Nagin dals pajais survart', value: 'Auters pajais' },
        ],
      },
    ],
    submit: 'Trametter',
    requirements: 'Pretensiuns',
    responsibilities: 'Responsabladads',
    uploadCV: 'Chargiar si CV',
    viewMore: 'Mussar dapli',
    linkInput: {
      smartExtract: 'Extracziun intelligenta:',
      apply: 'Applitgar',
      pasteHelper: 'Encollai l\'URL cumpletta per l\'extracziun automatica dal num d\'utilisader',
      placeholderDefault: '---',
    },
  },

  // Māori
  mi: {
    header: {
      badge: 'NGĀ MAHI',
      title: {
        part1: 'Hono Mai',
        highlighted: 'Waihanga, Hono, Auaha, Haere Tonu.',
      },
      description:
        'Tūhurahia ngā āheinga whakahihiko kia hono mai ki tō mātou rōpū auaha, kia āwhina hoki ki te hanga i te anamata o te kimi hua e whakahaerehia ana e te AI.',
    },
    positions: {
      sectionBadge: 'Ngā Tūranga Tuwhera',
      sectionTitle: {
        part1: 'Kimihia Tō',
        highlighted: 'Tūranga Tino Pai',
      },
      sectionDescription:
        'Tūhurahia ngā āheinga whakahihiko kia hono mai ki tō mātou rōpū auaha, kia āwhina hoki ki te hanga i te anamata o te kimi hua e whakahaerehia ana e te AI.',
    },
    noPositions: {
      title: {
        part1: 'Kāore he Tūranga Tuwhera',
        highlighted: 'I Tēnei Wā',
      },
      description:
        'E rapu tonu ana mātou i ngā tāngata pūkenga. Hoki mai ā muri, whakapā tika mai rānei ki a mātou ki te kōrero mō ngā āheinga ā muri.',
      contactButton: 'Whakapā Mai',
    },
    careers: 'Ngā Mahi',
    carrerFounderMessage: `E mōhio ana au he uaua i ētahi wā te whakarite i tētahi pukapuka mā tōna kōpaki, ā, e ngākau titikaha ana mātou ki te arotake i ia tono ki te tika me te tōkeke. Mēnā he tūhono tūmanako, ka whakapā tere mātou ki a koe. Mēnā e kaha ana tō whakaaro ki tō hono ki tō mātou rōpū, tēnā whakapā tika mai ki a mātou i runga i LinkedIn. E mihi ana mātou ki te wā i whakapau koe ki te tono.
    
    -Oli, Kaihanga o Inception`,
    careerIntroduction:
      "Ko tā mātou kaupapa i Inception he āwhina i ngā kiritaki puta noa i te ao kia kitea ngā hua pai rawa atu i tō rātou rohe mā te atamai hangahanga. Me tēnei whāinga hoki, e mahi ana mātou ki te āwhina i ngā kaipakihi iti ā-rohe ki te whakapai ake i tō rātou noho ipurangi.\n\nHono mai ki a mātou ki te whakatau i ngā raruraru o te ao tūturu mā te AI, mā te āwhina i ngā pakihi ā-rohe me te mahi i ngā hangarau hiranga. Ka taea e tātou te whai pānga nui ki ngā hapori me ngā kiritaki.",
    careerMeta: {
      title: 'Mahi - Hono Mai ki inceptionai.ca',
      description:
        'Hono mai ki a mātou ki te whakatau i ngā raruraru o te ao tūturu mā te AI mā te huri i ngā ōhanga ā-rohe me te tuku hua hiranga. Ka taea e tātou te whai pānga nui ki ngā hapori me ngā kiritaki.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Ō Mātou Uara',
      details: [
        {
          title: 'Hiahia Kiritaki',
          description:
            'Ka whakatairanga mātou i ngā wheako tino pai ki ō mātou kiritaki, e hāngai ana ki tā mātou kaupapa ki te hono i a rātou ki ngā hua pai rawa.',
        },
        {
          title: 'Auahatanga',
          description:
            'Ia rā, ka whakamahi mātou i ō mātou āheitanga AI me ngā raraunga nui ki te auaha me te whakapai ake i ngā huarahi e hono ai mātou i ngā kiritaki ki ngā hua kounga teitei.',
        },
        {
          title: 'Ahurea',
          description:
            'Ko tō mātou ahurea ako, mahi tahi, pūataata, whakamātau, me te hiahia e akiaki ana i a mātou kia whakapai ake tonu, kia whakatutuki i tā mātou kaupapa.',
        },
        {
          title: 'Arotahi Whāinga',
          description:
            'He ngākau titikaha, he arotahi whāinga, e whai ana mātou i te hiranga ki te whakatutuki i tā mātou kaupapa o te hono i ngā kiritaki ki ngā hua pai rawa.',
        },
        {
          title: 'Hapori',
          description:
            'Ka hanga, ka poipoi mātou i tētahi hapori tautoko e tipu tahi ana, he mea nui ki te whakatutuki i tā mātou kaupapa ā-ao.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Ingoa Tuatahi', required: true },
      { id: 'last_name', label: 'Ingoa Whānau', required: true },
      { id: 'email', label: 'Wāhitau Īmēra', type: 'email', required: true },
      { id: 'phone', label: 'Nama Waea', required: true },
      { id: 'full_address', label: 'Wāhitau Katoa (tae atu ki te whenua)', required: true },
      { id: 'desired_salary', label: 'Utu Hiahia', required: true },
      { id: 'start_date', label: 'Rā Tīmata Tūmanako', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Kōpaki Mahi', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Kei tētahi o ēnei whenua koe e noho ana i tēnei wā:',
        name: 'country',
        options: [
          { label: 'Kānata', value: 'CA' },
          { label: 'Hononga o Amerika', value: 'US' },
          { label: 'Kāore i ngā mea o runga ake nei', value: 'Whenua Kē' },
        ],
      },
    ],
    submit: 'Tuku',
    requirements: 'Ngā Herenga',
    responsibilities: 'Ngā Kawenga',
    uploadCV: 'Tukuna CV',
    viewMore: 'Whakaatu Anō',
    linkInput: {
      smartExtract: 'Tangohanga atamai:',
      apply: 'Tonoa',
      pasteHelper: 'Whakapiri URL katoa hei tangohanga aunoa o te ingoa kaiwhakamahi',
      placeholderDefault: '---',
    },
  },
  // Irish
  ga: {
    header: {
      badge: 'GAIRMEACHA',
      title: {
        part1: 'Bí Linn',
        highlighted: 'Cruthaigh, Ceangail, Nuálaigh, agus Téigh Níos Faide.',
      },
      description:
        'Féach ar dheiseanna spreagúla chun dul isteach inár bhfoireann nuálach agus cabhrú le todhchaí aimsiú táirgí le cumhacht IS a mhúnlú.',
    },
    positions: {
      sectionBadge: 'Poist Oscailte',
      sectionTitle: {
        part1: 'Aimsigh Do',
        highlighted: 'Ról Foirfe',
      },
      sectionDescription:
        'Féach ar dheiseanna spreagúla chun dul isteach inár bhfoireann nuálach agus cabhrú le todhchaí aimsiú táirgí le cumhacht IS a mhúnlú.',
    },
    noPositions: {
      title: {
        part1: 'Níl Aon Phoist Oscailte',
        highlighted: 'Faoi Láthair',
      },
      description:
        'Bímid i gcónaí ag lorg daoine cumasacha. Tar ar ais go luath nó déan teagmháil linn go díreach chun deiseanna amach anseo a phlé.',
      contactButton: 'Déan Teagmháil',
    },
    careers: 'Gairmeacha',
    carrerFounderMessage: `Aithním go mbíonn sé deacair uaireanta breithiúnas a thabhairt ar leabhar ón gclúdach, agus táimid tiomanta gach iarratas a mheasúnú go cúramach agus go cothrom. Má tá oiriúnacht fhéideartha ann, déanfaimid teagmháil leat go pras. Má mhothaíonn tú go láidir faoi d'ailíniú lenár bhfoireann, déan teagmháil linn go díreach ar LinkedIn. Táimid buíoch as an am a thóg tú chun iarratas a dhéanamh.

    -Oli, Bunaitheoir Inception`,
    careerIntroduction:
      "Is é ár misean ag Inception cabhrú le tomhaltóirí ar fud an domhain na táirgí is fearr ina réigiún a aimsiú le hintleacht shaorga. Leis an sprioc sin i gcuimhne, táimid ag déanamh ár ndícheall freisin cabhrú le húinéirí gnólachtaí beaga áitiúla a láithreacht ar líne a fheabhsú.\n\nBí linn chun fadhbanna an tsaoil a réiteach le IS trí chabhrú le gnólachtaí áitiúla agus ag obair ar theicneolaíochtaí den scoth. Le chéile, is féidir linn tionchar fiúntach a imirt ar phobail agus ar thomhaltóirí araon.",
    careerMeta: {
      title: 'Gairmréim - Bí Linn ag inceptionai.ca',
      description:
        'Bí linn chun fadhbanna an tsaoil a réiteach le IS trí gheilleagair áitiúla a athrú agus táirgí den scoth a sheachadadh. Le chéile, is féidir linn tionchar fiúntach a imirt ar phobail agus ar thomhaltóirí araon.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Ár Luachanna',
      details: [
        {
          title: 'Obsessed leis an gCustaiméir',
          description:
            'Cuirimid tús áite le heispéiris gan sárú a chur ar fáil dár gcustaiméirí, ag teacht lenár misean iad a nascadh leis na táirgí is fearr.',
        },
        {
          title: 'Nuálaíocht',
          description:
            'Gach lá, bainimid leas as ár gcumais IS agus sonraí ollmhóra chun nuálaíocht a dhéanamh agus feabhas a chur ar na bealaí a nascaimid tomhaltóirí le táirgí ardchaighdeáin.',
        },
        {
          title: 'Cultúr',
          description:
            'Spreagann ár gcultúr foghlama, comhoibrithe, trédhearcachta, turgnaimh agus paisin muid chun feabhas a chur go leanúnach agus ár misean a chomhlíonadh.',
        },
        {
          title: 'Dírithe ar Spriocanna',
          description:
            'Tiomanta agus dírithe ar spriocanna, déanaimid iarracht barr feabhais a bhaint amach ag comhlíonadh ár misin tomhaltóirí a nascadh le táirgí den scoth.',
        },
        {
          title: 'Pobal',
          description:
            'Tógaimid agus cothaímid pobal tacúil a bhláthaíonn le chéile, rud atá ríthábhachtach chun ár misean domhanda a chomhlíonadh.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Céadainm', required: true },
      { id: 'last_name', label: 'Sloinne', required: true },
      { id: 'email', label: 'Seoladh Ríomhphoist', type: 'email', required: true },
      { id: 'phone', label: 'Uimhir Ghutháin', required: true },
      { id: 'full_address', label: 'Seoladh Iomlán (tír san áireamh)', required: true },
      { id: 'desired_salary', label: 'Tuarastal Inmhianaithe', required: true },
      { id: 'start_date', label: 'Dáta Tosaithe Féideartha', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Punann', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'An bhfuil tú lonnaithe i gceann de na tíortha seo faoi láthair:',
        name: 'country',
        options: [
          { label: 'Ceanada', value: 'CA' },
          { label: 'Stáit Aontaithe Mheiriceá', value: 'US' },
          { label: 'Ceann ar bith díobh thuas', value: 'Tíortha Eile' },
        ],
      },
    ],
    submit: 'Seol',
    requirements: 'Riachtanais',
    responsibilities: 'Freagrachtaí',
    uploadCV: 'Uaslódáil CV',
    viewMore: 'Taispeáin Níos Mó',
    linkInput: {
      smartExtract: 'Eastóscadh cliste:',
      apply: 'Cuir i bhFeidhm',
      pasteHelper: 'Greamaigh URL iomlán le haghaidh eastóscadh ainm úsáideora uathoibríoch',
      placeholderDefault: '---',
    },
  },
  // Polish
  pl: {
    header: {
      badge: 'KARIERA',
      title: {
        part1: 'Dołącz do nas',
        highlighted: 'Twórz, Łącz, Innowuj i Idź Dalej.',
      },
      description:
        'Odkryj fascynujące możliwości dołączenia do naszego innowacyjnego zespołu i pomocy w kształtowaniu przyszłości odkrywania produktów opartego na AI.',
    },
    positions: {
      sectionBadge: 'Otwarte stanowiska',
      sectionTitle: {
        part1: 'Znajdź swoją',
        highlighted: 'Idealną rolę',
      },
      sectionDescription:
        'Odkryj fascynujące możliwości dołączenia do naszego innowacyjnego zespołu i pomocy w kształtowaniu przyszłości odkrywania produktów opartego na AI.',
    },
    noPositions: {
      title: {
        part1: 'Brak otwartych stanowisk',
        highlighted: 'W tej chwili',
      },
      description:
        'Zawsze poszukujemy utalentowanych osób. Proszę wkrótce sprawdzić ponownie lub skontaktować się z nami bezpośrednio w celu omówienia przyszłych możliwości.',
      contactButton: 'Skontaktuj się',
    },
    careers: 'Kariera',
    carrerFounderMessage: `Zdaję sobie sprawę, że czasami trudno jest oceniać książkę po okładce, dlatego zobowiązujemy się do dokładnego i uczciwego przeglądu każdego zgłoszenia. Jeśli istnieje potencjalne dopasowanie, skontaktujemy się z Państwem niezwłocznie. Jeśli są Państwo mocno przekonani o swoim dopasowaniu do naszego zespołu, prosimy o bezpośredni kontakt na LinkedIn. Doceniamy czas poświęcony na złożenie aplikacji.
    
    -Oli, Założyciel Inception`,
    careerIntroduction:
      "Naszą misją w Inception jest pomaganie konsumentom na całym świecie w znajdowaniu najlepszych produktów w ich regionie za pomocą sztucznej inteligencji. Mając ten cel na uwadze, dokładamy również wszelkich starań, aby pomóc właścicielom lokalnych małych firm w poprawie ich obecności w Internecie.\n\nDołącz do nas, aby rozwiązywać rzeczywiste problemy za pomocą AI, pomagając lokalnym firmom i pracując nad wybitnymi technologiami. Razem możemy wywrzeć znaczący wpływ zarówno na społeczności, jak i na konsumentów.",
    careerMeta: {
      title: 'Kariera - Dołącz do nas w inceptionai.ca',
      description:
        'Dołącz do nas, aby rozwiązywać rzeczywiste problemy za pomocą AI, transformując lokalne gospodarki i dostarczając wybitne produkty. Razem możemy wywrzeć znaczący wpływ zarówno na społeczności, jak i na konsumentów.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Nasze wartości',
      details: [
        {
          title: 'Zorientowanie na klienta',
          description:
            'Priorytetem jest dla nas dostarczanie niezrównanych doświadczeń naszym klientom, zgodnie z naszą misją łączenia ich z najlepszymi produktami.',
        },
        {
          title: 'Innowacja',
          description:
            'Każdego dnia wykorzystujemy nasze możliwości AI i ogromne zbiory danych, aby innowować i ulepszać sposoby łączenia konsumentów z produktami najwyższej jakości.',
        },
        {
          title: 'Kultura',
          description:
            'Nasza kultura uczenia się, współpracy, przejrzystości, eksperymentowania i pasji napędza nas do ciągłego doskonalenia i realizacji naszej misji.',
        },
        {
          title: 'Zorientowanie na cele',
          description:
            'Oddani i zorientowani na cele, dążymy do doskonałości w realizacji naszej misji łączenia konsumentów z najlepszymi produktami.',
        },
        {
          title: 'Społeczność',
          description:
            'Budujemy i pielęgnujemy wspierającą społeczność, która razem się rozwija, co jest kluczowe dla realizacji naszej globalnej misji.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Imię', required: true },
      { id: 'last_name', label: 'Nazwisko', required: true },
      { id: 'email', label: 'Adres e-mail', type: 'email', required: true },
      { id: 'phone', label: 'Numer telefonu', required: true },
      { id: 'full_address', label: 'Pełny adres (wraz z krajem)', required: true },
      { id: 'desired_salary', label: 'Oczekiwane wynagrodzenie', required: true },
      { id: 'start_date', label: 'Potencjalna data rozpoczęcia', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portfolio', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Czy obecnie mieszka Pan/Pani w jednym z tych krajów:',
        name: 'country',
        options: [
          { label: 'Kanada', value: 'CA' },
          { label: 'Stany Zjednoczone', value: 'US' },
          { label: 'Żaden z powyższych', value: 'Inne kraje' },
        ],
      },
    ],
    submit: 'Wyślij',
    requirements: 'Wymagania',
    responsibilities: 'Obowiązki',
    uploadCV: 'Prześlij CV',
    viewMore: 'Pokaż więcej',
    linkInput: {
      smartExtract: 'Inteligentna ekstrakcja:',
      apply: 'Zastosuj',
      pasteHelper: 'Wklej pełny URL w celu automatycznego wyodrębnienia nazwy użytkownika',
      placeholderDefault: '---',
    },
  },

  // Catalan
  ca: {
    header: {
      badge: 'CARRERES',
      title: {
        part1: 'Uniu-vos a nosaltres',
        highlighted: 'Crear, Connectar, Innovar i Anar Més Enllà.',
      },
      description:
        "Exploreu oportunitats apassionants per unir-vos al nostre equip innovador i ajudar a donar forma al futur del descobriment de productes impulsat per la IA.",
    },
    positions: {
      sectionBadge: 'Posicions obertes',
      sectionTitle: {
        part1: 'Trobeu el vostre',
        highlighted: 'Rol perfecte',
      },
      sectionDescription:
        "Exploreu oportunitats apassionants per unir-vos al nostre equip innovador i ajudar a donar forma al futur del descobriment de productes impulsat per la IA.",
    },
    noPositions: {
      title: {
        part1: 'Cap posició oberta',
        highlighted: 'En aquest moment',
      },
      description:
        "Sempre estem a la recerca de persones amb talent. Torneu aviat o poseu-vos en contacte directament amb nosaltres per parlar d'oportunitats futures.",
      contactButton: 'Contacteu-nos',
    },
    careers: 'Carreres',
    carrerFounderMessage: `Reconec que de vegades és difícil jutjar un llibre per la coberta, i ens comprometem a revisar cada candidatura de manera completa i justa. Si hi ha una possible coincidència, ens posarem en contacte amb vosaltres ràpidament. Si sentiu un fort alineament amb el nostre equip, poseu-vos en contacte directament amb nosaltres a LinkedIn. Agraïm el temps que heu dedicat a presentar la vostra candidatura.
    
    -Oli, Fundador d'Inception`,
    careerIntroduction:
      "La nostra missió a Inception és ajudar els consumidors d'arreu del món a trobar els millors productes de la seva regió amb intel·ligència artificial. Amb aquest objectiu en ment, també fem el possible per ajudar els propietaris de petites empreses locals a millorar la seva presència en línia.\n\nUniu-vos a nosaltres per resoldre problemes reals amb IA ajudant empreses locals i treballant en tecnologies extraordinàries. Junts, podem tenir un impacte significatiu tant en les comunitats com en els consumidors.",
    careerMeta: {
      title: "Carrera - Uniu-vos a nosaltres a inceptionai.ca",
      description:
        "Uniu-vos a nosaltres per resoldre problemes reals amb IA transformant economies locals i oferint productes extraordinaris. Junts, podem tenir un impacte significatiu tant en les comunitats com en els consumidors.",
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'Els nostres valors',
      details: [
        {
          title: 'Obsessionats pel client',
          description:
            "Prioritzem oferir experiències inigualables als nostres clients, alineats amb la nostra missió de connectar-los amb els millors productes.",
        },
        {
          title: 'Innovació',
          description:
            "Cada dia aprofitem les nostres capacitats d'IA i dades per innovar i millorar les maneres com connectem els consumidors amb productes d'alta qualitat.",
        },
        {
          title: 'Cultura',
          description:
            "La nostra cultura d'aprenentatge, col·laboració, transparència, experimentació i passió ens impulsa a millorar contínuament i complir la nostra missió.",
        },
        {
          title: 'Orientats a objectius',
          description:
            "Dedicats i orientats a objectius, busquem l'excel·lència en el compliment de la nostra missió de connectar els consumidors amb productes superiors.",
        },
        {
          title: 'Comunitat',
          description:
            "Construïm i nodrim una comunitat de suport que prospera conjuntament, essencial per complir la nostra missió global.",
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'Nom', required: true },
      { id: 'last_name', label: 'Cognoms', required: true },
      { id: 'email', label: "Adreça de correu electrònic", type: 'email', required: true },
      { id: 'phone', label: 'Número de telèfon', required: true },
      { id: 'full_address', label: 'Adreça completa (incloent-hi el país)', required: true },
      { id: 'desired_salary', label: 'Salari desitjat', required: true },
      { id: 'start_date', label: "Data d'inici potencial", required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'Portafoli', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'Residiu actualment en algun d\'aquests països:',
        name: 'country',
        options: [
          { label: 'Canadà', value: 'CA' },
          { label: 'Estats Units', value: 'US' },
          { label: 'Cap dels anteriors', value: 'Altres països' },
        ],
      },
    ],
    submit: 'Enviar',
    requirements: 'Requisits',
    responsibilities: 'Responsabilitats',
    uploadCV: 'Pujar CV',
    viewMore: 'Mostrar més',
    linkInput: {
      smartExtract: "Extracció intel·ligent:",
      apply: 'Aplicar',
      pasteHelper: "Enganxeu l'URL complet per a l'extracció automàtica del nom d'usuari",
      placeholderDefault: '---',
    },
  },

  te: {
    header: {
      badge: 'కెరీర్లు',
      title: {
        part1: 'మాతో చేరండి',
        highlighted: 'సృష్టించండి, కనెక్ట్ చేయండి, ఆవిష్కరించండి మరియు అంతకు మించి వెళ్ళండి.',
      },
      description:
        'AI-ఆధారిత ఉత్పత్తి ఆవిష్కరణ భవిష్యత్తును రూపొందించడంలో సహాయపడే మా ఆవిష్కరణ బృందంలో చేరడానికి ఉత్తేజకరమైన అవకాశాలను అన్వేషించండి.',
    },
    positions: {
      sectionBadge: 'ఓపెన్ పొజిషన్లు',
      sectionTitle: {
        part1: 'మీ',
        highlighted: 'సరైన పాత్రను కనుగొనండి',
      },
      sectionDescription:
        'AI-ఆధారిత ఉత్పత్తి ఆవిష్కరణ భవిష్యత్తును రూపొందించడంలో సహాయపడే మా ఆవిష్కరణ బృందంలో చేరడానికి ఉత్తేజకరమైన అవకాశాలను అన్వేషించండి.',
    },
    noPositions: {
      title: {
        part1: 'ప్రస్తుతం ఓపెన్ పొజిషన్లు',
        highlighted: 'లేవు',
      },
      description:
        'మేము ఎల్లప్పుడూ ప్రతిభావంతులైన వ్యక్తులను వెతుకుతున్నాము. త్వరలో తిరిగి తనిఖీ చేయండి లేదా భవిష్యత్ అవకాశాలను చర్చించడానికి నేరుగా మమ్మల్ని సంప్రదించండి.',
      contactButton: 'సంప్రదించండి',
    },
    careers: 'కెరీర్లు',
    carrerFounderMessage: `పుస్తకాన్ని దాని అట్టచూసి నిర్ణయించడం కొన్నిసార్లు కష్టమని నేను గుర్తిస్తాను, మరియు ప్రతి దరఖాస్తును క్షుణ్ణంగా మరియు న్యాయంగా సమీక్షించడానికి మేము కట్టుబడి ఉన్నాము. సంభావ్య అనుకూలత ఉంటే, మేము మిమ్మల్ని వెంటనే సంప్రదిస్తాము. మా బృందంతో మీ అనుసంధానం గురించి మీకు బలమైన నమ్మకం ఉంటే, దయచేసి LinkedIn లో నేరుగా మమ్మల్ని సంప్రదించండి. దరఖాస్తు చేయడానికి మీరు వెచ్చించిన సమయాన్ని మేము అభినందిస్తున్నాము.
    
    -Oli, Inception వ్యవస్థాపకుడు`,
    careerIntroduction:
      "Inception లో మా లక్ష్యం ప్రపంచవ్యాప్తంగా వినియోగదారులకు కృత్రిమ మేధస్సుతో వారి ప్రాంతంలో ఉత్తమ ఉత్పత్తులను కనుగొనడంలో సహాయపడటం. ఆ లక్ష్యంతో, స్థానిక చిన్న వ్యాపార యజమానులు వారి ఆన్‌లైన్ ఉనికిని మెరుగుపరచడంలో కూడా మేము మా వంతు ప్రయత్నం చేస్తున్నాము.\n\nస్థానిక వ్యాపారాలకు సహాయపడటం మరియు అద్భుతమైన సాంకేతికతలపై పనిచేయడం ద్వారా AI తో నిజ-ప్రపంచ సమస్యలను పరిష్కరించడంలో మాతో చేరండి. కలిసి, సమాజాలు మరియు వినియోగదారులు ఇద్దరిపై మేము అర్థవంతమైన ప్రభావం చేయగలము.",
    careerMeta: {
      title: 'కెరీర్ - inceptionai.ca లో మాతో చేరండి',
      description:
        'స్థానిక ఆర్థిక వ్యవస్థలను మార్చడం మరియు అద్భుతమైన ఉత్పత్తులను అందించడం ద్వారా AI తో నిజ-ప్రపంచ సమస్యలను పరిష్కరించడంలో మాతో చేరండి.',
    },
    cultureValues: {
      component: 'CultureValues',
      title: 'మా విలువలు',
      details: [
        {
          title: 'కస్టమర్ పట్ల అంకితభావం',
          description:
            'మా కస్టమర్లకు అసమానమైన అనుభవాలను అందించడానికి మేము ప్రాధాన్యత ఇస్తాము, వారిని ఉత్తమ ఉత్పత్తులతో అనుసంధానించే మా లక్ష్యానికి అనుగుణంగా.',
        },
        {
          title: 'ఆవిష్కరణ',
          description:
            'ప్రతిరోజూ, వినియోగదారులను అధిక-నాణ్యత ఉత్పత్తులతో అనుసంధానించే విధానాలను ఆవిష్కరించడానికి మరియు మెరుగుపరచడానికి మేము మా AI మరియు భారీ డేటా సామర్థ్యాలను ఉపయోగిస్తాము.',
        },
        {
          title: 'సంస్కృతి',
          description:
            'నేర్చుకోవడం, సహకారం, పారదర్శకత, ప్రయోగం మరియు ఉత్సాహం యొక్క మా సంస్కృతి నిరంతరం మెరుగుపడటానికి మరియు మా లక్ష్యాన్ని నెరవేర్చడానికి మాకు స్ఫూర్తినిస్తుంది.',
        },
        {
          title: 'లక్ష్య-ఆధారిత',
          description:
            'అంకితభావం మరియు లక్ష్య-ఆధారిత, వినియోగదారులను ఉన్నతమైన ఉత్పత్తులతో అనుసంధానించే మా లక్ష్యాన్ని చేరుకోవడంలో మేము శ్రేష్ఠత కోసం కృషి చేస్తాము.',
        },
        {
          title: 'సమాజం',
          description:
            'మా ప్రపంచ లక్ష్యాన్ని నెరవేర్చడానికి కీలకమైన, కలిసి అభివృద్ధి చెందే సహాయక సమాజాన్ని మేము నిర్మిస్తాము మరియు పెంచుతాము.',
        },
      ],
    },
    fields: [
      { id: 'fname', label: 'మొదటి పేరు', required: true },
      { id: 'last_name', label: 'చివరి పేరు', required: true },
      { id: 'email', label: 'ఇమెయిల్ చిరునామా', type: 'email', required: true },
      { id: 'phone', label: 'ఫోన్ నంబర్', required: true },
      { id: 'full_address', label: 'పూర్తి చిరునామా (దేశంతో సహా)', required: true },
      { id: 'desired_salary', label: 'కోరుకున్న జీతం', required: true },
      { id: 'start_date', label: 'సంభావ్య ప్రారంభ తేదీ', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X (Twitter)', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'పోర్ట్‌ఫోలియో', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: 'మీరు ప్రస్తుతం ఈ దేశాలలో ఏదైనా ఒకదానిలో ఉన్నారా:',
        name: 'country',
        options: [
          { label: 'కెనడా', value: 'CA' },
          { label: 'యునైటెడ్ స్టేట్స్', value: 'US' },
          { label: 'పైవేవీ కావు', value: 'ఇతర దేశాలు' },
        ],
      },
    ],
    submit: 'సబ్మిట్ చేయండి',
    requirements: 'అవసరాలు',
    responsibilities: 'బాధ్యతలు',
    uploadCV: 'CV అప్‌లోడ్ చేయండి',
    viewMore: 'మరిన్ని చూపించండి',
    linkInput: {
      smartExtract: 'స్మార్ట్ ఎక్స్‌ట్రాక్ట్:',
      apply: 'వర్తింపజేయండి',
      pasteHelper: 'ఆటోమేటిక్ యూజర్‌నేమ్ ఎక్స్‌ట్రాక్షన్ కోసం పూర్తి URL పేస్ట్ చేయండి',
      placeholderDefault: '---',
    },
  },

  // Japanese
  ja: {
    header: {
      badge: '採用情報',
      title: {
        part1: '私たちと一緒に',
        highlighted: '創造し、つながり、革新し、その先へ。',
      },
      description:
        'AIを活用したプロダクトディスカバリーの未来を共に創る、革新的なチームの一員になりませんか。',
    },
    positions: {
      sectionBadge: '募集中のポジション',
      sectionTitle: {
        part1: 'あなたに最適な',
        highlighted: 'キャリアを見つける',
      },
      sectionDescription:
        'AIを活用したプロダクトディスカバリーの未来を共に創る、革新的なチームの一員になりませんか。',
    },
    noPositions: {
      title: {
        part1: '現在募集中のポジションは',
        highlighted: 'ありません',
      },
      description:
        '優秀な人材を常に求めています。近日中にあらためてご確認いただくか、今後の採用機会についてお気軽にお問い合わせください。',
      contactButton: 'お問い合わせ',
    },
    careers: 'キャリア',
    carrerFounderMessage: `人を外見だけで判断するのは難しいことだと私自身も理解しています。すべてのご応募を丁寧かつ公正に選考することをお約束いたします。マッチする可能性がある場合は、速やかにご連絡いたします。私たちのチームへの強い想いをお持ちでしたら、LinkedInからお気軽にご連絡ください。貴重なお時間を割いてご応募いただき、心より感謝申し上げます。
    
    -Oli、Inception 創業者`,
    careerIntroduction:
      "Inceptionのミッションは、AIの力を活用して世界中の消費者がそれぞれの地域で最高のプロダクトに出会えるようにすることです。この目標のもと、地域の中小企業オーナーのオンラインプレゼンス向上にも全力で取り組んでいます。\n\n地域ビジネスを支援し、最先端のテクノロジーに携わりながら、AIで現実世界の課題を解決する仲間になりませんか。共に、コミュニティと消費者の双方に大きなインパクトを生み出しましょう。",
    careerMeta: {
      title: '採用情報 ― inceptionai.caで私たちと共に',
      description:
        'AIで現実世界の課題を解決し、地域経済を変革し、優れたプロダクトを届ける仲間を募集しています。共に、コミュニティと消費者の双方に大きなインパクトを生み出しましょう。',
    },
    cultureValues: {
      component: 'CultureValues',
      title: '私たちのバリュー',
      details: [
        {
          title: 'カスタマーオブセッション',
          description:
            '最高のプロダクトとお客様をつなぐというミッションのもと、比類のない体験を提供することを最優先にしています。',
        },
        {
          title: 'イノベーション',
          description:
            '日々AIとビッグデータの力を活かし、消費者と高品質なプロダクトをつなぐ新しい方法を革新し続けています。',
        },
        {
          title: 'カルチャー',
          description:
            '学び合い、協働、透明性、実験精神、そして情熱の文化が、私たちの継続的な成長とミッション達成を支えています。',
        },
        {
          title: 'ゴールドリブン',
          description:
            '目標達成に向けてひたむきに取り組み、消費者と優れたプロダクトをつなぐミッションにおいて卓越した成果を追求します。',
        },
        {
          title: 'コミュニティ',
          description:
            'グローバルなミッション達成に不可欠な、互いに支え合い共に成長するコミュニティを構築し育んでいます。',
        },
      ],
    },
    fields: [
      { id: 'fname', label: '名', required: true },
      { id: 'last_name', label: '姓', required: true },
      { id: 'email', label: 'メールアドレス', type: 'email', required: true },
      { id: 'phone', label: '電話番号', required: true },
      { id: 'full_address', label: '住所（国名を含む）', required: true },
      { id: 'desired_salary', label: '希望年収', required: true },
      { id: 'start_date', label: '入社可能日', required: true },
      { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/', required: false },
      { id: 'twitter', label: 'X（Twitter）', prefix: 'x.com/', required: false },
      { id: 'github', label: 'GitHub', prefix: 'github.com/', required: false },
      { id: 'portfolio', label: 'ポートフォリオ', prefix: 'https://', required: false },
    ],
    radioGroups: [
      {
        question: '現在お住まいの国を選択してください：',
        name: 'country',
        options: [
          { label: 'カナダ', value: 'CA' },
          { label: 'アメリカ合衆国', value: 'US' },
          { label: '上記以外', value: 'その他の国' },
        ],
      },
    ],
    submit: '応募する',
    requirements: '応募要件',
    responsibilities: '業務内容',
    uploadCV: '履歴書をアップロード',
    viewMore: 'もっと見る',
    linkInput: {
      smartExtract: 'スマート抽出：',
      apply: '適用',
      pasteHelper: 'ユーザー名を自動抽出するにはURLを貼り付けてください',
      placeholderDefault: '---',
    },
  },
};

const careersPageContent: Record<LanguageType, CareersPageContent> =
  proxyLanguageRecord<CareersPageContent>(careersPageContentCore);

export const getCareersPageContent = (locale: LocaleDetails): CareersPageContent => {
  return careersPageContent[locale.languageCode] || careersPageContent.en;
};
